import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock,
  User,
  UserPlus,
  MapPin,
  Loader2,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Grid3X3,
  List,
  Edit,
  Trash2,
  Phone,
  Mail,
  CreditCard,
  FileText,
  Calendar as CalendarCheck,
  MessageSquare,
  MessageCircle,
  Bell,
  Ban,
  CalendarOff,
  Search
} from 'lucide-react';
import { format, addDays, addMinutes, parseISO, differenceInYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import AgendaCalendarView from '../components/AgendaCalendarView';
// Testar se import do AgendaSemanalView quebra
import AgendaSemanalView from '../components/AgendaSemanalView';
import { generateTermoDesligamento, downloadPDFCrossBrowser } from '../utils/pdfGenerator';
import { fetchDocumentSignature } from '../utils/documentSignature';
import { verificarFeriado } from '../utils/feriados';

// Componentes extraídos (Refatoração Fase 1)
import CancelamentoDialog, { initialCancelamentoState } from '../components/agendamentos/CancelamentoDialog';
import BloqueioAgendaDialog, { initialBloqueioDialogState, initialBloqueioFormState, TIPOS_BLOQUEIO } from '../components/agendamentos/BloqueioAgendaDialog';
import SerieDeleteDialog, { initialSerieDeleteState } from '../components/agendamentos/SerieDeleteDialog';
import DetalhesAgendamentoDialog, { initialDetailsDialogState } from '../components/agendamentos/DetalhesAgendamentoDialog';

// Helper para parsear data do Supabase (que vem com +00:00) como horário local
// O Supabase salva como UTC, mas queremos interpretar como horário local
const parseLocalDateTime = (dateString) => {
  if (!dateString) return null;
  // Remove o timezone (+00:00 ou Z) e parseia como local
  const withoutTz = dateString.replace(/\+00:00$/, '').replace(/Z$/, '');
  return new Date(withoutTz);
};

// Helper para formatar data/hora para exibição
const formatDateTime = (dateString, formatStr = "dd/MM 'às' HH:mm") => {
  const date = parseLocalDateTime(dateString);
  if (!date || isNaN(date.getTime())) return '';
  return format(date, formatStr, { locale: ptBR });
};

const AgendamentosPage = () => {
  const { authFetch, isAdminOrRecepcao, user, profissionalAtivoId } = useAuth();

  // 2026-09-04 — Chapéu ATIVO (para termo de desligamento e outros PDFs
  // gerados por profissional multi-especialidade). Sem isso, uma Musicoterapeuta
  // como a Dayene emitia termo carimbado com Psicologia.
  const profissionalAtivo = (() => {
    const profs = user?.profissionais || (user?.profissional ? [user.profissional] : []);
    if (!profs.length) return user?.profissional || null;
    if (profissionalAtivoId) {
      const match = profs.find(p => p.id === profissionalAtivoId);
      if (match) return match;
    }
    return profs[0];
  })();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ler parâmetro data da URL (ex: ?data=2026-04-20)
  const dataParam = searchParams.get('data');
  const dataInicial = dataParam ? new Date(dataParam + 'T12:00:00') : new Date();
  
  const [agendamentos, setAgendamentos] = useState([]);
  const [agendamentosSemana, setAgendamentosSemana] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [profissionaisFiltrados, setProfissionaisFiltrados] = useState([]);

  // 2026-02 — Set de usuario_ids que atuam em >1 especialidade DISTINTA (não
  // conta linhas duplicadas com mesma espec.), para desambiguar o nome nos
  // cards (Recepção e Profissional mobile). Só qualifica com sufixo "—
  // Especialidade" quando é o caso; profissionais mono-especialidade seguem
  // exibindo apenas o nome (sem ruído).
  const usuariosMultiEsp = useMemo(() => {
    const espsPorUser = {};
    profissionais.forEach(p => {
      const uid = p.usuario_id || p.usuarios?.id;
      const esp = p.especialidade;
      if (!uid || !esp) return;
      if (!espsPorUser[uid]) espsPorUser[uid] = new Set();
      espsPorUser[uid].add(esp);
    });
    return new Set(Object.entries(espsPorUser).filter(([, s]) => s.size > 1).map(([uid]) => uid));
  }, [profissionais]);
  const [idadePacienteAtual, setIdadePacienteAtual] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [salas, setSalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSemana, setLoadingSemana] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dataInicial);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Profissional começa com lista diária, Admin/Recepção também começa com lista diária
  const [activeView, setActiveView] = useState('lista');
  const [editingAgendamento, setEditingAgendamento] = useState(null);
  const [searchPacienteLista, setSearchPacienteLista] = useState(''); // Pesquisa na Lista Diária
  
  // Estado para novo paciente rápido
  const [isNovoPaciente, setIsNovoPaciente] = useState(false);
  const [novoPacienteData, setNovoPacienteData] = useState({
    nome_completo: '',
    telefone_responsavel: '',
    data_nascimento: '',
    convenio_id: ''
  });
  
  // Estado para horários disponíveis do profissional
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  
  // Estado para janelas livres (novo sistema flexível)
  const [janelasLivres, setJanelasLivres] = useState({
    janelas: [],
    proximo_livre: null,
    ocupados: [],
    periodos: []
  });
  const [horarioManual, setHorarioManual] = useState('');
  
  // Estado para dias da semana em que o profissional tem locação de sala
  // Formato: [1, 2, 3, 4, 5] (Segunda=1, Terça=2, etc.)
  const [diasDisponiveis, setDiasDisponiveis] = useState([1, 2, 3, 4, 5, 6]); // Todos por padrão
  
  // Estado para exclusão de série
  const [serieDeleteDialog, setSerieDeleteDialog] = useState({
    open: false,
    agendamento: null,
    serieInfo: null,
    loading: false
  });
  
  // Estado para modal de cancelamento com justificativa
  const [cancelamentoDialog, setCancelamentoDialog] = useState({
    open: false,
    agendamento: null,
    justificativa: '',
    loading: false,
    // Novos campos para cancelamento condicional
    step: 'escolha', // 'escolha' | 'simples' | 'definitivo'
    tipoCancelamento: null, // 'simples' | 'definitivo'
    // Campos do Termo de Desligamento
    motivoDesligamento: '',
    encaminhamento: ''
  });
  
  // Estado para visualizar detalhes do agendamento (Item 1)
  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    agendamento: null
  });
  
  // Estado para modal de remanejamento (Profissional)
  const [remanejamentoDialog, setRemanejamentoDialog] = useState({
    open: false,
    agendamento: null,
    data: null,
    horaInicio: '',
    horariosDisponiveis: [],
    janelasLivres: { janelas: [], proximo_livre: null, ocupados: [], periodos: [] },
    diasDisponiveis: [],
    loadingDias: false,
    loadingHorarios: false,
    submitting: false
  });

  // Estado para modal de TROCA de horários entre 2 pacientes (feature 2026-08).
  // Fluxo: profissional clica em "Trocar" no card do agendamento A, o modal
  // lista os outros agendamentos elegíveis do mesmo profissional próximos e
  // ele escolhe o agendamento B. Backend faz swap atômico.
  const [swapDialog, setSwapDialog] = useState({
    open: false,
    agendamentoA: null,       // objeto do agendamento clicado
    candidatos: [],           // outros agendamentos elegíveis do mesmo prof
    selectedBId: '',          // id do agendamento B escolhido
    loading: false,
    submitting: false,
  });

  // Estado para forçar refresh do calendário
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  
  // Estado para Bloqueios de Agenda
  const [bloqueios, setBloqueios] = useState([]);
  const [bloqueioDialog, setBloqueioDialog] = useState({
    open: false,
    loading: false,
    editingBloqueio: null
  });
  const [bloqueioForm, setBloqueioForm] = useState({
    profissional_id: '',
    data_inicio: '',
    data_fim: '',
    hora_inicio: '',
    hora_fim: '',
    dia_inteiro: false,
    motivo: '',
    tipo: 'bloqueio'
  });
  
  // TIPOS_BLOQUEIO movido para ../components/agendamentos/BloqueioAgendaDialog.jsx
  
  // Form state simplificado
  const [formData, setFormData] = useState({
    paciente_id: '',
    profissional_id: '',
    data: new Date(),
    hora_inicio: '',
    duracao: 40, // Duração em minutos (padrão 40min)
    convenio_id: '',
    observacoes: '',
    especialidade: '',
    recorrencia: 'nenhuma',
    recorrencia_fim: '',
    is_encaixe: false,
    justificativa_encaixe: ''
  });

  // Particular explícito não pode compartilhar o estado vazio da seleção obrigatória.
  const convenioIdSelecionado = formData.convenio_id || (isNovoPaciente ? novoPacienteData.convenio_id : '');
  const convenioSelecionadoAtivo = convenios.find(c => c.id === convenioIdSelecionado);
  const cadastroParticular = convenios.find(c => c.nome?.trim().toLowerCase() === 'particular');
  const cobrancaParticular = convenioIdSelecionado === 'particular'
    || (!!cadastroParticular && convenioSelecionadoAtivo?.id === cadastroParticular.id);
  const convenioHistoricoMantido = !!editingAgendamento?.convenio_id
    && editingAgendamento.convenio_id === convenioIdSelecionado
    && editingAgendamento.paciente_id === formData.paciente_id
    && !convenioSelecionadoAtivo;
  const cobrancaSelecionadaValida = !!convenioIdSelecionado
    && (cobrancaParticular || !!convenioSelecionadoAtivo || convenioHistoricoMantido);

  const DURACOES = [
    { value: 30, label: '30 minutos' },
    { value: 40, label: '40 minutos (padrão)' },
    { value: 50, label: '50 minutos' },
    { value: 60, label: '1 hora' },
    { value: 120, label: '2 horas (bloqueio)' }
  ];

  const RECORRENCIAS = [
    { value: 'nenhuma', label: 'Sem recorrência' },
    { value: 'semanal', label: 'Semanal' },
    { value: 'quinzenal', label: 'Quinzenal' },
    { value: 'mensal', label: 'Mensal' }
  ];

  const ESPECIALIDADES = [
    'Psicologia',
    'Fonoaudiologia', 
    'Terapia Ocupacional',
    'Nutrição',
    'Psicopedagogia',
    'Musicoterapia',
    'Avaliação Neuropsicológica'
  ];

  const STATUS_CONFIG = {
    agendado: { label: 'Agendado', color: '#5C6B64', icon: Clock },
    aguardando: { label: 'Aguardando', color: '#D4A373', icon: AlertCircle },
    em_atendimento: { label: 'Em Atendimento', color: '#4A6B5D', icon: PlayCircle },
    finalizado: { label: 'Finalizado', color: '#7B9E87', icon: CheckCircle },
    cancelado: { label: 'Cancelado', color: '#B75D69', icon: XCircle },
    falta: { label: 'Falta', color: '#B75D69', icon: XCircle }
  };

  const fetchData = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Profissionais usam endpoints diferentes
      const pacientesEndpoint = user?.perfil === 'profissional' ? '/pacientes/meus' : '/pacientes';
      const profissionaisEndpoint = user?.perfil === 'profissional' ? '/profissionais/lista/basica' : '/profissionais';
      
      const [agendRes, profRes, pacRes, convRes, salasRes, bloqRes] = await Promise.all([
        authFetch(`/agendamentos?data_inicio=${dateStr}&data_fim=${dateStr}`),
        authFetch(profissionaisEndpoint).catch(() => ({ data: [] })),
        authFetch(pacientesEndpoint + (pacientesEndpoint.includes('?') ? '&' : '?') + 'slim=true').catch(() => ({ data: [] })),
        authFetch('/convenios'),
        authFetch('/salas?slim=true').catch(() => ({ data: [] })),
        authFetch('/bloqueios-agenda').catch(() => ({ data: [] })),
      ]);
      
      setAgendamentos(agendRes.data || []);
      setProfissionais(profRes.data || []);
      setProfissionaisFiltrados(profRes.data || []);
      setPacientes(pacRes.data || []);
      setConvenios(convRes.data || []);
      setSalas((salasRes.data || []).filter(s => s.ativa !== false));
      setBloqueios(bloqRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error('Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };
  
  // Buscar bloqueios de agenda
  const fetchBloqueios = async () => {
    try {
      const res = await authFetch('/bloqueios-agenda');
      setBloqueios(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar bloqueios:', err);
    }
  };
  
  // Abrir modal de bloqueio
  const openBloqueioModal = (bloqueio = null) => {
    if (bloqueio) {
      // Editando bloqueio existente
      setBloqueioForm({
        profissional_id: bloqueio.profissional_id || '',
        data_inicio: bloqueio.data_inicio?.split('T')[0] || '',
        data_fim: bloqueio.data_fim?.split('T')[0] || '',
        hora_inicio: bloqueio.hora_inicio || '',
        hora_fim: bloqueio.hora_fim || '',
        dia_inteiro: bloqueio.dia_inteiro || false,
        motivo: bloqueio.motivo || '',
        tipo: bloqueio.tipo || 'bloqueio'
      });
      setBloqueioDialog({ open: true, loading: false, editingBloqueio: bloqueio });
    } else {
      // Novo bloqueio - buscar profissional_id de forma correta
      const today = format(new Date(), 'yyyy-MM-dd');
      let profId = '';
      
      if (user?.perfil === 'profissional') {
        // Profissional logado - usar o ID do próprio profissional
        profId = user?.profissional?.id || '';
      }
      
      setBloqueioForm({
        profissional_id: profId,
        data_inicio: today,
        data_fim: today,
        hora_inicio: '08:00',
        hora_fim: '18:00',
        dia_inteiro: false,
        motivo: '',
        tipo: 'bloqueio'
      });
      setBloqueioDialog({ open: true, loading: false, editingBloqueio: null });
    }
  };
  
  // Salvar bloqueio
  const handleSaveBloqueio = async () => {
    if (!bloqueioForm.profissional_id) {
      toast.error('Selecione o profissional');
      return;
    }
    if (!bloqueioForm.data_inicio || !bloqueioForm.data_fim) {
      toast.error('Informe as datas de início e fim');
      return;
    }
    if (!bloqueioForm.dia_inteiro && (!bloqueioForm.hora_inicio || !bloqueioForm.hora_fim)) {
      toast.error('Informe os horários ou marque "Dia Inteiro"');
      return;
    }
    
    setBloqueioDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const payload = {
        profissional_id: bloqueioForm.profissional_id,
        data_inicio: bloqueioForm.data_inicio,
        data_fim: bloqueioForm.data_fim,
        hora_inicio: bloqueioForm.dia_inteiro ? null : bloqueioForm.hora_inicio,
        hora_fim: bloqueioForm.dia_inteiro ? null : bloqueioForm.hora_fim,
        dia_inteiro: bloqueioForm.dia_inteiro,
        motivo: bloqueioForm.motivo || null,
        tipo: bloqueioForm.tipo
      };
      
      if (bloqueioDialog.editingBloqueio) {
        // Atualizar
        await authFetch(`/bloqueios-agenda/${bloqueioDialog.editingBloqueio.id}`, {
          method: 'PUT',
          data: payload
        });
        toast.success('Bloqueio atualizado!');
      } else {
        // Criar
        await authFetch('/bloqueios-agenda', {
          method: 'POST',
          data: payload
        });
        toast.success('Bloqueio criado!');
      }
      
      setBloqueioDialog({ open: false, loading: false, editingBloqueio: null });
      fetchBloqueios();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (err) {
      // Governança 423 → interceptor global já trata.
      if (err?.response?.status !== 423) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.mensagem || 'Erro ao salvar bloqueio');
        toast.error(msg);
      }
    } finally {
      setBloqueioDialog(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Excluir bloqueio
  const handleDeleteBloqueio = async (bloqueioId) => {
    if (!window.confirm('Tem certeza que deseja remover este bloqueio?')) return;
    
    try {
      await authFetch(`/bloqueios-agenda/${bloqueioId}`, { method: 'DELETE' });
      toast.success('Bloqueio removido!');
      fetchBloqueios();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (err) {
      toast.error('Erro ao remover bloqueio');
    }
  };

  // Função para buscar agendamentos da semana (chamada pelo componente AgendaSemanalView)
  const fetchAgendamentosSemana = async (weekStart, weekEnd) => {
    try {
      setLoadingSemana(true);
      const startStr = format(weekStart, 'yyyy-MM-dd');
      const endStr = format(weekEnd, 'yyyy-MM-dd');
      
      const response = await authFetch(`/agendamentos?data_inicio=${startStr}&data_fim=${endStr}`);
      setAgendamentosSemana(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar semana:', err);
    } finally {
      setLoadingSemana(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  // Auto-refresh silencioso dos agendamentos do dia (30s) - atualiza status sem recarregar a página
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return; // não atualiza com a aba em segundo plano
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      authFetch(`/agendamentos?data_inicio=${dateStr}&data_fim=${dateStr}`, { suppressAuthRedirect: true })
        .then(res => setAgendamentos(res.data || []))
        .catch(() => {}); // silencioso - próxima tentativa em 30s
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Atalho: se URL tem ?novo=1&paciente_id=X, abre modal de novo agendamento com paciente pré-selecionado
  useEffect(() => {
    const novoParam = searchParams.get('novo');
    const pacienteParam = searchParams.get('paciente_id');
    if (novoParam === '1' && pacienteParam && pacientes.length > 0) {
      const paciente = pacientes.find(p => p.id === pacienteParam);
      if (paciente) {
        setEditingAgendamento(null);
        setIsNovoPaciente(false);
        setFormData(prev => ({
          ...prev,
          paciente_id: pacienteParam,
          convenio_id: paciente.convenio_id || '',
          data: new Date(),
        }));
        setIsDialogOpen(true);
        // Consumir o atalho pelo Router (sem deixar searchParams antigo no estado).
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('novo');
        nextParams.delete('paciente_id');
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [pacientes, searchParams, setSearchParams]);


  // Filtrar profissionais baseado nos critérios do paciente/agendamento
  const filtrarProfissionais = () => {
    let filtered = [...profissionais];
    
    // Obter dados do paciente selecionado ou do novo paciente
    let convenioId = '';
    let idadePaciente = null;
    
    if (isNovoPaciente && novoPacienteData.data_nascimento) {
      const nascimento = new Date(novoPacienteData.data_nascimento);
      const hoje = new Date();
      idadePaciente = Math.floor((hoje - nascimento) / (365.25 * 24 * 60 * 60 * 1000));
      convenioId = novoPacienteData.convenio_id;
    } else if (formData.paciente_id) {
      const paciente = pacientes.find(p => p.id === formData.paciente_id);
      if (paciente) {
        if (paciente.data_nascimento) {
          const nascimento = new Date(paciente.data_nascimento);
          const hoje = new Date();
          idadePaciente = Math.floor((hoje - nascimento) / (365.25 * 24 * 60 * 60 * 1000));
        }
        convenioId = paciente.convenio_id;
      }
    }
    
    // Filtrar por especialidade (TRAVA)
    if (formData.especialidade) {
      filtered = filtered.filter(p => p.especialidade === formData.especialidade);
    }
    
    // Filtrar por convênio selecionado (TRAVA - mantém filtro rígido)
    const convenioSelecionado = convenioIdSelecionado;
    if (convenioSelecionado && !cobrancaParticular && !convenioHistoricoMantido) {
      filtered = filtered.filter(p => {
        const conveniosAceitos = p.convenios_aceitos || [];
        return conveniosAceitos.some(c => c.id === convenioSelecionado);
      });
    }
    
    // Marcar compatibilidade de faixa etária (AVISO - apenas visual, sem filtrar)
    filtered = filtered.map(p => {
      const min = p.faixa_etaria_min ?? 0;
      const max = p.faixa_etaria_max ?? 100;
      const dentroFaixaEtaria = idadePaciente === null || (idadePaciente >= min && idadePaciente <= max);
      return { ...p, _dentroFaixaEtaria: dentroFaixaEtaria };
    });
    
    // Ordenar: compatíveis com faixa etária primeiro
    filtered.sort((a, b) => {
      if (a._dentroFaixaEtaria && !b._dentroFaixaEtaria) return -1;
      if (!a._dentroFaixaEtaria && b._dentroFaixaEtaria) return 1;
      return 0;
    });
    
    setProfissionaisFiltrados(filtered);
    setIdadePacienteAtual(idadePaciente);
    
    // Se o profissional atual não está na lista filtrada (por convênio), limpar seleção
    if (formData.profissional_id && !filtered.find(p => p.id === formData.profissional_id)) {
      setFormData(prev => ({ ...prev, profissional_id: '', hora_inicio: '' }));
      setHorariosDisponiveis([]);
    }
  };

  // Buscar horários disponíveis quando profissional ou data mudam
  const buscarHorariosDisponiveis = async () => {
    if (!formData.profissional_id || !formData.data) return;
    
    setLoadingHorarios(true);
    try {
      const dataStr = format(formData.data, 'yyyy-MM-dd');
      
      // Buscar horários tradicionais (para compatibilidade)
      const result = await authFetch(`/profissionais/${formData.profissional_id}/horarios-disponiveis?data=${dataStr}`);
      setHorariosDisponiveis(result.data?.horarios || []);
      
      // Se o usuário ainda não ajustou manualmente a duração, buscar a duração padrão do profissional (moda histórica)
      let duracaoParam = formData.duracao || 40;
      if (!formData._duracaoAjustadaManual) {
        try {
          const dp = await authFetch(`/profissionais/${formData.profissional_id}/duracao-padrao`);
          const duracaoSugerida = dp.data?.duracao_minutos;
          if (duracaoSugerida && duracaoSugerida !== formData.duracao) {
            duracaoParam = duracaoSugerida;
            setFormData(prev => ({ ...prev, duracao: duracaoSugerida }));
          }
        } catch (e) {
          // Silencioso; mantém default
        }
      }
      
      // Buscar janelas livres (novo sistema flexível)
      const janelasResult = await authFetch(`/profissionais/${formData.profissional_id}/janelas-livres?data=${dataStr}&duracao_minutos=${duracaoParam}`);
      setJanelasLivres(janelasResult.data || { janelas: [], proximo_livre: null, ocupados: [], periodos: [] });
      
      // Se tem próximo livre, sugerir automaticamente
      if (janelasResult.data?.proximo_livre && !formData.hora_inicio) {
        setHorarioManual(janelasResult.data.proximo_livre);
      }
      
      if ((result.data?.horarios || []).length === 0) {
        toast.info(result.data?.message || 'Profissional não atende neste dia');
      }
    } catch (err) {
      console.error('Erro ao buscar horários:', err);
      setHorariosDisponiveis([]);
      setJanelasLivres({ janelas: [], proximo_livre: null, ocupados: [], periodos: [] });
    } finally {
      setLoadingHorarios(false);
    }
  };

  // Buscar dias da semana em que o profissional tem locação de sala
  const buscarDiasDisponiveis = async (profissionalId) => {
    if (!profissionalId) {
      setDiasDisponiveis([1, 2, 3, 4, 5, 6]); // Reset para todos os dias
      return;
    }
    
    try {
      const result = await authFetch(`/profissionais/${profissionalId}/dias-disponiveis`);
      if (result.data?.dias && result.data.dias.length > 0) {
        setDiasDisponiveis(result.data.dias);
      } else {
        setDiasDisponiveis([]); // Nenhum dia disponível
      }
    } catch (err) {
      console.error('Erro ao buscar dias disponíveis:', err);
      setDiasDisponiveis([1, 2, 3, 4, 5, 6]); // Fallback para todos os dias
    }
  };

  // Buscar dias disponíveis quando profissional muda
  useEffect(() => {
    buscarDiasDisponiveis(formData.profissional_id);
  }, [formData.profissional_id]);

  useEffect(() => {
    if (formData.profissional_id && formData.data) {
      buscarHorariosDisponiveis();
    }
  }, [formData.profissional_id, formData.data]);

  // Atualizar filtro quando critérios mudam
  // IMPORTANTE: pacientes e profissionais nas dependências para evitar race condition
  // onde o filtro é executado antes dos dados carregarem (bug da faixa etária incorreta)
  useEffect(() => {
    filtrarProfissionais();
  }, [formData.paciente_id, formData.especialidade, formData.convenio_id, isNovoPaciente, novoPacienteData.data_nascimento, novoPacienteData.convenio_id, pacientes, profissionais]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let pacienteId = formData.paciente_id;

      if (!cobrancaSelecionadaValida) {
        toast.error('Selecione um convênio ativo ou Particular antes de agendar.');
        return;
      }
      
      // Se é novo paciente, criar primeiro
      if (isNovoPaciente) {
        if (!novoPacienteData.nome_completo) {
          toast.error('Nome do paciente é obrigatório');
          setIsSubmitting(false);
          return;
        }

        if (!novoPacienteData.convenio_id) {
          toast.error('Selecione o convênio do cadastro do paciente, incluindo Particular quando aplicável.');
          return;
        }
        
        const pacientePayload = {
          nome_completo: novoPacienteData.nome_completo,
          telefone_responsavel: novoPacienteData.telefone_responsavel || null,
          data_nascimento: novoPacienteData.data_nascimento || null,
          convenio_id: novoPacienteData.convenio_id || null
        };
        
        // 2026-02 — Guard-rail anti-duplicata: se backend retornar 409 com
        // codigo PACIENTE_DUPLICADO, pergunta ao usuário via window.confirm
        // e reenvia com ?confirmar_duplicata=true se ele autorizar.
        let pacienteRes;
        try {
          pacienteRes = await authFetch('/pacientes', {
            method: 'POST',
            data: pacientePayload
          });
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 409 && detail?.codigo === 'PACIENTE_DUPLICADO') {
            const lista = (detail.duplicatas || [])
              .map((d) => `• ${d.nome_completo}${d.convenio_nome ? ' — ' + d.convenio_nome : ''}${d.ativo === false ? ' (inativo)' : ''}`)
              .join('\n');
            const ok = window.confirm(
              `⚠️ ${detail.mensagem}\n\n${lista}\n\n` +
              `Verifique se é a mesma pessoa antes de duplicar.\n\n` +
              `OK = cadastrar mesmo assim\n` +
              `Cancelar = voltar (recomendado — buscar o paciente na lista)`
            );
            if (!ok) {
              setIsSubmitting(false);
              return;
            }
            pacienteRes = await authFetch('/pacientes?confirmar_duplicata=true', {
              method: 'POST',
              data: pacientePayload
            });
          } else {
            throw err;
          }
        }
        
        pacienteId = pacienteRes.data.id;
        toast.success('Paciente cadastrado!');
      }
      
      if (!pacienteId) {
        toast.error('Selecione ou cadastre um paciente');
        setIsSubmitting(false);
        return;
      }
      
      // Validar justificativa de encaixe
      if (formData.is_encaixe && !formData.justificativa_encaixe?.trim()) {
        toast.error('Informe a justificativa do encaixe');
        setIsSubmitting(false);
        return;
      }

      // Construir data/hora
      const [hora, minuto] = formData.hora_inicio.split(':').map(Number);
      const dataHoraInicio = new Date(formData.data);
      dataHoraInicio.setHours(hora, minuto, 0, 0);
      
      // Usar duração selecionada pelo usuário
      const duracao = formData.duracao || 40;
      
      const dataHoraFim = addMinutes(dataHoraInicio, duracao);

      // Formatar data/hora SEM conversão para UTC (mantém horário local)
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      // Determinar tipo de atendimento
      const convenioId = convenioIdSelecionado;
      const isParticular = cobrancaParticular;

      const data = {
        paciente_id: pacienteId,
        profissional_id: formData.profissional_id,
        sala_id: null, // Será determinado automaticamente pela locação
        data_hora_inicio: formatLocalDateTime(dataHoraInicio),
        data_hora_fim: formatLocalDateTime(dataHoraFim),
        tipo_atendimento: isParticular ? 'particular' : 'convenio',
        convenio_id: isParticular ? null : convenioId,
        observacoes: formData.observacoes || null,
        recorrencia: formData.recorrencia !== 'nenhuma' ? formData.recorrencia : null,
        recorrencia_fim: formData.recorrencia_fim || null,
        is_encaixe: formData.is_encaixe || false,
        justificativa_encaixe: formData.is_encaixe ? formData.justificativa_encaixe : null
      };

      if (editingAgendamento) {
        // Atualizar agendamento existente
        const putRes = await authFetch(`/agendamentos/${editingAgendamento.id}`, {
          method: 'PUT',
          data
        });
        toast.success('Agendamento atualizado com sucesso!');

        // Atualiza localmente o agendamento nas visões diária e semanal (evita estado stale)
        const updated = putRes?.data;
        if (updated && updated.id) {
          setAgendamentos(prev =>
            prev.map(a => (a.id === updated.id ? { ...a, ...updated } : a))
          );
          setAgendamentosSemana(prev =>
            prev.map(a => (a.id === updated.id ? { ...a, ...updated } : a))
          );
        }
      } else {
        // Criar novo agendamento
        const response = await authFetch('/agendamentos', {
          method: 'POST',
          data
        });
        
        const totalCriados = response.data?.total_criados || 1;
        const conflitos = response.data?.conflitos || [];
        
        if (conflitos.length > 0) {
          // Separar conflitos por tipo
          const feriadosInfo = conflitos.filter(c => c.tipo === 'feriado');
          const errosReais = conflitos.filter(c => c.tipo !== 'feriado');
          
          if (errosReais.length > 0) {
            toast.warning(`${totalCriados} agendamento(s) criado(s). ${errosReais.length} conflito(s) de horário.`);
            errosReais.forEach(c => {
              toast.error(`Conflito em ${c.data} às ${c.horario}: ${c.motivo}`);
            });
          } else {
            toast.success(`${totalCriados} agendamento(s) criado(s) com sucesso!`);
          }
          
          // Mostrar feriados como informativo (não é erro)
          if (feriadosInfo.length > 0) {
            toast.info(`📅 ${feriadosInfo.length} data(s) em feriado - agendado(s) como cancelado para possível reposição`);
          }
        } else {
          toast.success(`${totalCriados} agendamento(s) criado(s) com sucesso!`);
        }
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      const fallback = editingAgendamento ? 'Erro ao atualizar agendamento' : 'Erro ao criar agendamento';
      // 2026-08 Bug fix: backend pode retornar `detail` como OBJETO estruturado
      // (ex: governanca HTTP 423 devolve {codigo, mensagem, profissional_nome, ...}).
      // Antes o toast recebia o objeto direto e nao renderizava — Recepcao via
      // "tela branca" ao tentar agendar profissional bloqueado.
      const rawDetail = err.response?.data?.detail;
      let message;
      if (rawDetail && typeof rawDetail === 'object') {
        message = rawDetail.mensagem || rawDetail.detail || rawDetail.msg || JSON.stringify(rawDetail);
      } else {
        message = rawDetail || fallback;
      }
      // Governanca: destaca com toast mais longo e icone de bloqueio
      if (rawDetail?.codigo === 'AGENDA_DE_PROFISSIONAL_BLOQUEADA') {
        toast.error(message, {
          duration: 10000,
          description: 'Acione a coordenação para regularizar as pendências antes de novos agendamentos.'
        });
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Transições permitidas por perfil
  const TRANSICOES_PERMITIDAS = {
    administrador: {
      // Admin não tem restrições - pode fazer qualquer transição
      'agendado': ['aguardando', 'em_atendimento', 'finalizado', 'cancelado', 'falta'],
      'aguardando': ['agendado', 'em_atendimento', 'finalizado', 'cancelado', 'falta'],
      'em_atendimento': ['agendado', 'aguardando', 'finalizado', 'cancelado', 'falta'],
      'finalizado': ['agendado', 'aguardando', 'em_atendimento', 'cancelado', 'falta'],
      'cancelado': ['agendado', 'aguardando', 'em_atendimento', 'finalizado', 'falta'],
      'falta': ['agendado', 'aguardando', 'em_atendimento', 'finalizado', 'cancelado']
    },
    recepcao: {
      'agendado': ['aguardando', 'cancelado', 'falta'],
      'aguardando': ['cancelado', 'falta', 'agendado'],
      'em_atendimento': ['cancelado'],
      'finalizado': [],
      'cancelado': ['agendado'],
      'falta': ['agendado']
    },
    profissional: {
      'agendado': ['cancelado'],
      'aguardando': ['em_atendimento', 'cancelado'],
      'em_atendimento': ['finalizado', 'cancelado'],
      'finalizado': [],
      'cancelado': [],
      'falta': []
    }
  };

  const handleUpdateStatus = async (agendamentoId, novoStatus, justificativa = null, agendamentoObj = null) => {
    // Se é cancelamento e não tem justificativa, abrir modal
    if (novoStatus === 'cancelado' && !justificativa) {
      // Buscar agendamento nas listas disponíveis ou usar o objeto passado
      const ag = agendamentoObj || 
                 agendamentos.find(a => a.id === agendamentoId) || 
                 agendamentosSemana.find(a => a.id === agendamentoId);
      
      if (!ag) {
        toast.error('Agendamento não encontrado');
        return;
      }
      
      // Profissional e Recepção: mostrar modal de escolha (simples vs definitivo)
      // Admin: ir direto para cancelamento simples
      const showEscolha = user?.perfil === 'profissional' || user?.perfil === 'recepcao';
      
      setCancelamentoDialog({ 
        open: true, 
        agendamento: ag, 
        justificativa: '', 
        loading: false,
        step: showEscolha ? 'escolha' : 'simples',
        tipoCancelamento: showEscolha ? null : 'simples',
        motivoDesligamento: '',
        encaminhamento: '',
        // Escopo padrão: apenas o profissional do agendamento atual (mais seguro)
        escopoDesligamento: 'profissional',
        escopoProfissionalId: ag?.profissional_id || null,
        escopoProfissionalNome: ag?.profissionais?.usuarios?.nome || ag?.profissionais?.nome || ''
      });
      return;
    }

    // Guard 2026-08: confirmação ao finalizar antes do horário previsto (evita
    // finalizações em massa antes do fim do atendimento, caso Sônia Brambilla 05/08).
    if (novoStatus === 'finalizado') {
      const ag = agendamentoObj ||
                 agendamentos.find(a => a.id === agendamentoId) ||
                 agendamentosSemana.find(a => a.id === agendamentoId);
      if (ag?.data_hora_fim) {
        const dhFim = new Date(ag.data_hora_fim);
        if (!isNaN(dhFim.getTime()) && new Date() < dhFim) {
          const horaFimStr = dhFim.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
          });
          const ok = window.confirm(
            `O atendimento está sendo finalizado antes do horário previsto (${horaFimStr}). Confirma?`
          );
          if (!ok) return;
        }
      }
    }

    // Guard 2026-08 (Márcia/Gabriela): confirmação ao marcar "aguardando" antes
    // do horário do agendamento. Se >24h, o backend bloqueia (guard de UI só
    // aparece para <24h). Admin continua isento (backend permite).
    if (novoStatus === 'aguardando' && user?.perfil !== 'administrador') {
      const ag = agendamentoObj ||
                 agendamentos.find(a => a.id === agendamentoId) ||
                 agendamentosSemana.find(a => a.id === agendamentoId);
      if (ag?.data_hora_inicio && ag.status !== 'aguardando') {
        const dhInicio = new Date(ag.data_hora_inicio);
        if (!isNaN(dhInicio.getTime())) {
          const diffMin = (dhInicio.getTime() - Date.now()) / 60000;
          // Se falta MAIS de 24h → backend bloqueia com 400; nem envia.
          if (diffMin > 24 * 60) {
            const horaStr = dhInicio.toLocaleString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
            });
            toast.error(
              `Não é possível marcar "Aguardando" com mais de 24h de antecedência. Agendamento: ${horaStr}.`,
              { duration: 8000 }
            );
            return;
          }
          // Se falta ENTRE 0h e 24h → confirma com o usuário.
          if (diffMin > 0 && diffMin <= 24 * 60) {
            const horaStr = dhInicio.toLocaleString('pt-BR', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            });
            const ok = window.confirm(
              `Este agendamento é para ${horaStr}. Confirma que o paciente já chegou à clínica?`
            );
            if (!ok) return;
          }
          // Se diffMin <= 0 (agendamento no horário ou já passou) → livre.
        }
      }
    }

    try {
      await authFetch(`/agendamentos/${agendamentoId}/status`, {
        method: 'PUT',
        data: { status: novoStatus, justificativa }
      });
      
      toast.success('Status atualizado!');
      fetchData();
    } catch (err) {
      const rawDetail = err.response?.data?.detail;
      let message;
      if (rawDetail && typeof rawDetail === 'object') {
        message = rawDetail.mensagem || rawDetail.detail || rawDetail.msg || 'Erro ao atualizar status';
      } else {
        message = rawDetail || 'Erro ao atualizar status';
      }
      if (rawDetail?.codigo === 'AGENDA_DE_PROFISSIONAL_BLOQUEADA') {
        toast.error(message, { duration: 10000, description: 'Acione a coordenação para regularizar as pendências.' });
      } else {
        toast.error(message);
      }
    }
  };

  const handleCancelarAgendamento = async () => {
    if (!cancelamentoDialog.justificativa.trim()) {
      toast.error('Justificativa é obrigatória para cancelamento');
      return;
    }
    
    // Validação adicional para cancelamento definitivo (todos os perfis)
    if (cancelamentoDialog.tipoCancelamento === 'definitivo' && !cancelamentoDialog.motivoDesligamento.trim()) {
      toast.error('Motivo do desligamento é obrigatório');
      return;
    }
    
    setCancelamentoDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const agendamento = cancelamentoDialog.agendamento;
      const justificativa = cancelamentoDialog.justificativa.trim();
      const isDefinitivo = cancelamentoDialog.tipoCancelamento === 'definitivo';
      const isProfissional = user?.perfil === 'profissional';
      
      if (isDefinitivo) {
        // ========== DESLIGAMENTO DEFINITIVO ==========
        console.log('Iniciando desligamento definitivo para paciente:', agendamento.paciente_id);
        
        // Validar dados do agendamento
        if (!agendamento.pacientes || !agendamento.pacientes.nome_completo) {
          console.error('Dados do paciente não disponíveis:', agendamento);
          toast.error('Dados do paciente não disponíveis. Tente atualizar a página.');
          setCancelamentoDialog(prev => ({ ...prev, loading: false }));
          return;
        }
        
        // 2026-02 — Rastreamento de sucesso real das etapas críticas.
        // Se o termo não persistir no backend, NÃO deletamos agendamentos
        // futuros — evita o "sucesso fantasma" (usuário achando que salvou
        // mas o documento não existe no sistema e o histórico some).
        let termoSalvoOk = false;
        let termoDocumentoId = null;
        let termoErroMensagem = null;
        let termoPDF = null;

        // 1. Gerar termo de desligamento (para TODOS os perfis)
        try {
          console.log('Gerando termo de desligamento...');
          // Consulta nova do chapéu ativo: não reutilizar rubrica de outro vínculo/cache.
          const assinaturaAtual = isProfissional
            ? await fetchDocumentSignature(authFetch, profissionalAtivo?.id)
            : null;
          const dadosTermo = {
            paciente: agendamento.pacientes,
            profissional: { 
              nome: user?.nome || (isProfissional ? 'Profissional' : 'Recepção'),
              especialidade: profissionalAtivo?.especialidade || null,
              crm_crp: profissionalAtivo?.registro_profissional || null,
              assinatura: assinaturaAtual,
              assinaturaObrigatoria: isProfissional
            },
            motivo: cancelamentoDialog.motivoDesligamento,
            encaminhamento: cancelamentoDialog.encaminhamento || null,
            dataUltimoAtendimento: agendamento.data_hora_inicio,
            realizadoPor: user?.perfil || 'sistema'
          };
          
          // Gerar uma vez e persistir antes de entregar. Se a assinatura falhar,
          // o guard abaixo aborta antes de alterar qualquer agendamento.
          termoPDF = await generateTermoDesligamento(dadosTermo);
          const pdfBase64 = termoPDF.output('datauristring');
          
          // Salvar registro do termo no backend para histórico (Docs Gerados)
          try {
            console.log('[DESLIGAMENTO] Salvando termo no backend...');
            
            // 2026-02 — Cap CLIENT-SIDE do payload total antes do POST:
            // se o pdf_base64 estiver acima de 3MB, DERRUBAR o pdf_base64 (salva
            // metadados sem o PDF anexado) para que o request passe pelo edge
            // do Cloudflare (que rejeita bodies >~4MB antes de chegar no backend
            // e gera "Network Error" opaco). Motivo comum: assinatura em cache
            // do browser ainda usa versão antiga gigante mesmo após update no DB.
            // Registrar aviso no console + toast informativo.
            const MAX_PDF_SIZE = 3 * 1024 * 1024;
            let pdfToSave = pdfBase64;
            
            if (pdfBase64 && pdfBase64.length > MAX_PDF_SIZE) {
              if (isProfissional) throw new Error('PDF do termo muito grande. O desligamento foi interrompido; reduza o conteúdo e tente novamente.');
              const sizeMB = (pdfBase64.length / 1024 / 1024).toFixed(2);
              console.warn(`[DESLIGAMENTO] PDF muito grande (${sizeMB}MB > 3MB), salvando apenas metadados. Provável causa: assinatura em cache antiga. Recomende logout+limpar cache ao usuário.`);
              toast.warning(
                `PDF do termo tem ${sizeMB}MB — muito grande para o servidor. ` +
                `Registrando apenas os metadados. ` +
                `Recomendação: faça logout, limpe cache do site e tente novamente para gerar com PDF completo.`,
                { duration: 10000 }
              );
              pdfToSave = null;
            }
            
            const result = await authFetch('/documentos-clinica', {
              method: 'POST',
              data: {
                paciente_id: agendamento.paciente_id,
                tipo: 'termo_desligamento',
                titulo: `Termo de Desligamento - ${cancelamentoDialog.motivoDesligamento.substring(0, 50)}`,
                dados: {
                  motivo: cancelamentoDialog.motivoDesligamento,
                  encaminhamento: cancelamentoDialog.encaminhamento || null,
                  agendamento_id: agendamento.id,
                  paciente_nome: agendamento.pacientes?.nome_completo || 'N/A',
                  realizado_por_nome: user?.nome || 'N/A',
                  realizado_por_perfil: user?.perfil || 'N/A'
                },
                pdf_base64: pdfToSave
              }
            });
            console.log('[DESLIGAMENTO] Termo salvo com sucesso:', result);
            // 2026-02 — só marca como salvo se retornou id de verdade
            const docId = result?.data?.id || result?.id;
            if (docId) {
              termoSalvoOk = true;
              termoDocumentoId = docId;
            } else {
              termoErroMensagem = 'Backend retornou sucesso sem ID do documento';
              console.error('[DESLIGAMENTO]', termoErroMensagem, result);
            }
          } catch (docErr) {
            console.error('[DESLIGAMENTO] ERRO ao salvar termo:', docErr);
            termoErroMensagem = docErr?.response?.data?.detail || docErr?.message || 'Erro ao registrar termo no sistema';
          }
        } catch (pdfErr) {
          console.error('[DESLIGAMENTO] Erro geral no PDF:', pdfErr);
          termoErroMensagem = pdfErr?.message || 'Erro ao gerar PDF';
        }

        // 2026-02 — Guard-rail: se termo NÃO foi salvo, ABORTA o desligamento
        // antes de deletar agendamentos. Sem termo = sem comprovante = não
        // podemos apagar a agenda futura silenciosamente.
        if (!termoSalvoOk) {
          console.error('[DESLIGAMENTO] Abortando: termo não persistiu.', { termoErroMensagem });
          toast.error(
            `❌ Termo NÃO foi salvo no sistema. Desligamento CANCELADO. ` +
            `Nenhum agendamento foi excluído. ` +
            (termoErroMensagem ? `Motivo: ${termoErroMensagem}. ` : '') +
            `Tente novamente ou contate o suporte.`,
            { duration: 12000, id: 'desligamento-erro' }
          );
          setCancelamentoDialog(prev => ({ ...prev, loading: false }));
          return;
        }

        // 2026-02 — Confirmação pós-save: garante que o documento realmente
        // aparece via GET (protege contra falhas silenciosas de replicação/cache).
        try {
          const confirm = await authFetch(
            `/documentos-clinica?paciente_id=${agendamento.paciente_id}&tipo=termo_desligamento`
          );
          const lista = confirm?.data || confirm || [];
          const achouId = Array.isArray(lista) && lista.some((d) => d.id === termoDocumentoId);
          if (!achouId) {
            console.error('[DESLIGAMENTO] Termo salvo mas não aparece no GET — abortando');
            toast.error(
              `❌ Termo foi criado mas não aparece na consulta. Desligamento CANCELADO ` +
              `por segurança. Verifique em Docs Gerados antes de tentar novamente.`,
              { duration: 12000 }
            );
            setCancelamentoDialog(prev => ({ ...prev, loading: false }));
            return;
          }
          console.log('[DESLIGAMENTO] Termo confirmado via GET:', termoDocumentoId);
        } catch (verifyErr) {
          console.warn('[DESLIGAMENTO] Falha ao verificar termo via GET, mas prossegue:', verifyErr);
          // Não bloqueia porque o POST retornou 200 com id — verificação é dupla-checagem
        }

        toast.success('Termo de Desligamento salvo com sucesso!', { duration: 4000 });

        // Download do mesmo PDF validado/salvo; nunca regenerar com outra assinatura.
        try {
          await downloadPDFCrossBrowser(termoPDF, `termo_desligamento_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        } catch (downloadError) {
          toast.warning('O termo foi salvo. Se o download não iniciou, baixe o original em Docs Gerados.');
        }

        // 2. EXCLUIR agendamentos futuros do paciente (só chega aqui se termo OK)
        // Escopo: Recepção/Admin podem ter optado por apenas o profissional do agendamento atual.
        try {
          const podeEscopo = user?.perfil === 'recepcao' || user?.perfil === 'administrador';
          const escoparPorProfissional = podeEscopo && cancelamentoDialog.escopoDesligamento === 'profissional' && cancelamentoDialog.escopoProfissionalId;
          const queryEscopo = escoparPorProfissional
            ? `?profissional_id=${cancelamentoDialog.escopoProfissionalId}`
            : '';
          console.log('[DESLIGAMENTO] Excluindo agendamentos do paciente:', agendamento.paciente_id, '| escopo:', escoparPorProfissional ? `prof ${cancelamentoDialog.escopoProfissionalId}` : 'clínica inteira');
          const deleteResult = await authFetch(`/agendamentos/excluir-por-paciente/${agendamento.paciente_id}${queryEscopo}`, {
            method: 'DELETE'
          });
          console.log('[DESLIGAMENTO] Agendamentos excluídos com sucesso:', deleteResult);
          toast.success('Agendamentos futuros excluídos!');
        } catch (delErr) {
          // Fallback: excluir apenas este agendamento e sua série
          console.error('[DESLIGAMENTO] Erro ao excluir por paciente, usando fallback:', delErr);
          try {
            const deleteSerie = !!agendamento.serie_id;
            await authFetch(`/agendamentos/${agendamento.id}?delete_serie=${deleteSerie}`, {
              method: 'DELETE'
            });
            console.log('[DESLIGAMENTO] Fallback executado: agendamento individual excluído');
          } catch (fallbackErr) {
            console.error('[DESLIGAMENTO] Fallback também falhou:', fallbackErr);
            toast.error(
              '⚠️ Termo foi salvo, MAS não foi possível excluir os agendamentos futuros. ' +
              'Exclua manualmente na agenda.',
              { duration: 12000 }
            );
          }
        }

        // 2026-08 — Notificação de desligamento para a recepção agora é criada
        // automaticamente pelo backend (DELETE /excluir-por-paciente) com
        // origem='sistema'. Antes o frontend chamava POST /mural/avisos sem
        // origem, caindo no Chat em vez do Mural.

        toast.success('Paciente desligado! Termo gerado e disponível em Docs Gerados.');
        
      } else {
        // ========== CANCELAMENTO SIMPLES ==========
        await authFetch(`/agendamentos/${agendamento.id}/status`, {
          method: 'PUT',
          data: { 
            status: 'cancelado', 
            justificativa: justificativa 
          }
        });

        // 2026-08 — Notificação de cancelamento para a recepção agora é criada
        // automaticamente pelo backend (PUT /agendamentos/{id}/status) com
        // origem='sistema'. Antes o frontend chamava POST /mural/avisos sem
        // origem, caindo no Chat em vez do Mural.

        toast.success('Agendamento cancelado!');
      }
      
      // Fechar TODOS os modais e atualizar dados
      setCancelamentoDialog({ 
        open: false, 
        agendamento: null, 
        justificativa: '', 
        loading: false,
        step: 'escolha',
        tipoCancelamento: null,
        motivoDesligamento: '',
        encaminhamento: '',
        escopoDesligamento: 'profissional',
        escopoProfissionalId: null,
        escopoProfissionalNome: ''
      });
      // Fechar também o modal de detalhes do agendamento
      setDetailsDialog({ open: false, agendamento: null });
      fetchData();
      
    } catch (err) {
      console.error('Erro no processamento de cancelamento:', err);
      // Tentar extrair mensagem de erro mais detalhada
      let message = 'Erro ao processar';
      const rawDetail = err.response?.data?.detail;
      if (rawDetail && typeof rawDetail === 'object') {
        message = rawDetail.mensagem || rawDetail.detail || JSON.stringify(rawDetail);
      } else if (rawDetail) {
        message = rawDetail;
      } else if (err.message) {
        message = err.message;
      } else if (typeof err === 'string') {
        message = err;
      }
      toast.error(message);
      setCancelamentoDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Obter transições permitidas para um agendamento
  const getTransicoesPermitidas = (statusAtual, perfil) => {
    const transicoes = TRANSICOES_PERMITIDAS[perfil] || {};
    return transicoes[statusAtual] || [];
  };

  const handleDeleteAgendamento = async (agendamento, deleteSerie = false) => {
    // Se já está no contexto do dialog de série, executa a exclusão
    if (serieDeleteDialog.open) {
      try {
        setSerieDeleteDialog(prev => ({ ...prev, loading: true }));
        
        await authFetch(`/agendamentos/${agendamento.id}?delete_serie=${deleteSerie}`, {
          method: 'DELETE'
        });
        
        const msg = deleteSerie 
          ? `${serieDeleteDialog.serieInfo?.agendamentos_futuros || 1} agendamento(s) da série excluído(s)!`
          : 'Agendamento excluído!';
        toast.success(msg);
        setSerieDeleteDialog({ open: false, agendamento: null, serieInfo: null, loading: false });
        setCalendarRefreshKey(prev => prev + 1);
        fetchData();
      } catch (err) {
        // Governança 423 → interceptor global já trata (toast + redirect).
        if (err?.response?.status !== 423) {
          const detail = err?.response?.data?.detail;
          const message = typeof detail === 'string'
            ? detail
            : (detail?.mensagem || 'Erro ao excluir');
          toast.error(message);
        }
        setSerieDeleteDialog(prev => ({ ...prev, loading: false }));
      }
      return;
    }
    
    // Verificar se faz parte de uma série
    if (agendamento.serie_id) {
      try {
        // Buscar informações da série
        const serieInfo = await authFetch(`/agendamentos/${agendamento.id}/serie`);
        
        if (serieInfo.is_serie && serieInfo.agendamentos_futuros > 1) {
          // Abrir dialog para escolher
          setSerieDeleteDialog({
            open: true,
            agendamento,
            serieInfo,
            loading: false
          });
          return;
        }
      } catch (err) {
        console.error('Erro ao verificar série:', err);
        // Se falhar, continua com exclusão normal
      }
    }
    
    // Exclusão simples (sem série ou série com apenas 1 item)
    const pacienteNome = agendamento.pacientes?.nome_completo || 'Paciente';
    const dataHora = formatDateTime(agendamento.data_hora_inicio, "dd/MM HH:mm");
    
    if (!window.confirm(`Tem certeza que deseja EXCLUIR PERMANENTEMENTE o agendamento de ${pacienteNome} em ${dataHora}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      await authFetch(`/agendamentos/${agendamento.id}`, {
        method: 'DELETE'
      });
      
      toast.success('Agendamento excluído!');
      setCalendarRefreshKey(prev => prev + 1);
      fetchData();
    } catch (err) {
      // Governança 423 → interceptor global já trata (toast + redirect).
      if (err?.response?.status !== 423) {
        const detail = err?.response?.data?.detail;
        const message = typeof detail === 'string'
          ? detail
          : (detail?.mensagem || 'Erro ao excluir agendamento');
        toast.error(message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      paciente_id: '',
      profissional_id: '',
      data: new Date(),
      hora_inicio: '',
      duracao: 40,
      convenio_id: '',
      observacoes: '',
      especialidade: '',
      recorrencia: 'nenhuma',
      recorrencia_fim: ''
    });
    setIsNovoPaciente(false);
    setNovoPacienteData({
      nome_completo: '',
      telefone_responsavel: '',
      data_nascimento: '',
      convenio_id: ''
    });
    setProfissionaisFiltrados(profissionais);
    setHorariosDisponiveis([]);
    setJanelasLivres({ janelas: [], proximo_livre: null, ocupados: [], periodos: [] });
    setHorarioManual('');
    setEditingAgendamento(null);
  };

  // ============================================
  // REMANEJAMENTO DE AGENDAMENTOS - PROFISSIONAL
  // ============================================
  
  // Função para abrir modal de remanejamento
  const handleOpenRemanejar = async (agendamento) => {
    // Calcular duração do agendamento
    const inicio = new Date(agendamento.data_hora_inicio);
    const fim = new Date(agendamento.data_hora_fim);
    let duracao = Math.round((fim - inicio) / 60000); // minutos

    // Fallback: se duração inválida (0/NaN/negativa), buscar duração padrão do profissional
    if (!duracao || duracao <= 0 || isNaN(duracao)) {
      try {
        const dp = await authFetch(`/profissionais/${agendamento.profissional_id}/duracao-padrao`);
        duracao = dp.data?.duracao_minutos || 40;
      } catch {
        duracao = 40;
      }
    }

    setRemanejamentoDialog({
      open: true,
      agendamento: { ...agendamento, duracao },
      data: null,
      horaInicio: '',
      horariosDisponiveis: [],
      janelasLivres: { janelas: [], proximo_livre: null, ocupados: [], periodos: [] },
      diasDisponiveis: [],
      loadingDias: true,
      loadingHorarios: false,
      submitting: false
    });
    
    // Buscar dias disponíveis do profissional
    // remanejamento=true: para profissional multi-modalidade (LOC + %),
    // considera todos os chapéus físicos ao pintar o calendário — permite
    // mover paciente APAS para dia LOC do mesmo profissional (bug Murilo 2026-08).
    try {
      const result = await authFetch(`/profissionais/${agendamento.profissional_id}/dias-disponiveis?remanejamento=true`);
      setRemanejamentoDialog(prev => ({
        ...prev,
        diasDisponiveis: result.data?.dias || [],
        loadingDias: false
      }));
    } catch (err) {
      console.error('Erro ao buscar dias disponíveis:', err);
      setRemanejamentoDialog(prev => ({ ...prev, diasDisponiveis: [], loadingDias: false }));
    }
  };

  // ============================================
  // TROCA DE HORÁRIOS (SWAP) — 2026-08
  // ============================================
  //
  // Abre o modal e busca candidatos: outros agendamentos do MESMO profissional
  // com status agendado/aguardando/falta, nos próximos 30 dias.
  const handleOpenSwap = async (agendamento) => {
    setSwapDialog({
      open: true,
      agendamentoA: agendamento,
      candidatos: [],
      selectedBId: '',
      loading: true,
      submitting: false,
    });

    try {
      // Busca agendamentos dos próximos 30 dias do mesmo profissional
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const daqui30 = new Date(hoje);
      daqui30.setDate(daqui30.getDate() + 30);

      const params = new URLSearchParams({
        data_inicio: format(hoje, 'yyyy-MM-dd'),
        data_fim: format(daqui30, 'yyyy-MM-dd'),
        profissional_id: agendamento.profissional_id,
      });
      const result = await authFetch(`/agendamentos?${params.toString()}`);
      const todos = Array.isArray(result.data) ? result.data : (result.data?.agendamentos || []);

      // Filtra: mesmo profissional (múltiplos chapéus da mesma pessoa via usuario_id
      // — o backend já filtra por profissional_id, então isso já vem certo), diferente
      // do próprio A, status permitido.
      const STATUS_OK = ['agendado', 'aguardando', 'falta'];
      const candidatos = todos
        .filter(x => x.id !== agendamento.id && STATUS_OK.includes(x.status))
        .sort((a, b) => new Date(a.data_hora_inicio) - new Date(b.data_hora_inicio));

      setSwapDialog(prev => ({ ...prev, candidatos, loading: false }));
    } catch (err) {
      console.error('Erro ao buscar candidatos para troca:', err);
      toast.error('Não foi possível carregar a lista de agendamentos para troca.');
      setSwapDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleConfirmSwap = async () => {
    const { agendamentoA, selectedBId, candidatos } = swapDialog;
    if (!agendamentoA?.id || !selectedBId) {
      toast.error('Selecione o agendamento para troca.');
      return;
    }
    const agB = candidatos.find(c => c.id === selectedBId);
    if (!agB) {
      toast.error('Agendamento selecionado não é válido.');
      return;
    }

    // Confirmação humana
    const nomeA = agendamentoA.pacientes?.nome_completo || 'Paciente A';
    const nomeB = agB.pacientes?.nome_completo || 'Paciente B';
    const horaA = formatDateTime(agendamentoA.data_hora_inicio, "dd/MM 'às' HH:mm");
    const horaB = formatDateTime(agB.data_hora_inicio, "dd/MM 'às' HH:mm");
    const ok = window.confirm(
      `Trocar horários?\n\n• ${nomeA} (${horaA}) → ${horaB}\n• ${nomeB} (${horaB}) → ${horaA}`
    );
    if (!ok) return;

    setSwapDialog(prev => ({ ...prev, submitting: true }));
    try {
      await authFetch(`/agendamentos/${agendamentoA.id}/trocar-com/${selectedBId}`, {
        method: 'POST',
      });
      toast.success(`Horários trocados: ${nomeA} ↔ ${nomeB}.`);
      setSwapDialog({
        open: false, agendamentoA: null, candidatos: [], selectedBId: '',
        loading: false, submitting: false,
      });
      // Recarrega dados
      setCalendarRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Erro na troca:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Erro ao trocar horários';
      toast.error(msg);
      setSwapDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  // Função para buscar horários quando a data muda no remanejamento
  const buscarHorariosRemanejamento = async (data) => {
    const agendamento = remanejamentoDialog.agendamento;
    if (!agendamento || !data) return;
    
    setRemanejamentoDialog(prev => ({ ...prev, loadingHorarios: true, horaInicio: '' }));
    
    try {
      const dataStr = format(data, 'yyyy-MM-dd');
      const duracao = agendamento.duracao || 40;
      
      // Buscar janelas livres
      // remanejamento=true: bug Murilo 2026-08 (ver /dias-disponiveis)
      const janelasResult = await authFetch(
        `/profissionais/${agendamento.profissional_id}/janelas-livres?data=${dataStr}&duracao_minutos=${duracao}&excluir_agendamento_id=${agendamento.id}&remanejamento=true`
      );
      
      setRemanejamentoDialog(prev => ({
        ...prev,
        data: data,
        janelasLivres: janelasResult.data || { janelas: [], proximo_livre: null, ocupados: [], periodos: [] },
        loadingHorarios: false
      }));
    } catch (err) {
      console.error('Erro ao buscar horários:', err);
      setRemanejamentoDialog(prev => ({
        ...prev,
        data: data,
        janelasLivres: { janelas: [], proximo_livre: null, ocupados: [], periodos: [] },
        loadingHorarios: false
      }));
      toast.error('Erro ao buscar horários disponíveis');
    }
  };
  
  // Função para executar o remanejamento
  const handleConfirmarRemanejamento = async () => {
    const { agendamento, data, horaInicio } = remanejamentoDialog;
    
    if (!data || !horaInicio) {
      toast.error('Selecione data e horário');
      return;
    }
    
    setRemanejamentoDialog(prev => ({ ...prev, submitting: true }));
    
    try {
      const duracao = agendamento.duracao || 40;
      const dataStr = format(data, 'yyyy-MM-dd');
      const novaDataHoraInicio = `${dataStr}T${horaInicio}:00`;
      
      // Calcular hora fim
      const [h, m] = horaInicio.split(':').map(Number);
      const inicioDate = new Date(2026, 0, 1, h, m);
      const fimDate = new Date(inicioDate.getTime() + duracao * 60000);
      const horaFim = `${String(fimDate.getHours()).padStart(2, '0')}:${String(fimDate.getMinutes()).padStart(2, '0')}`;
      const novaDataHoraFim = `${dataStr}T${horaFim}:00`;
      
      await authFetch(`/agendamentos/${agendamento.id}/remanejar`, {
        method: 'POST',
        data: {
          nova_data_hora_inicio: novaDataHoraInicio,
          nova_data_hora_fim: novaDataHoraFim
        }
      });
      
      toast.success('Agendamento remanejado com sucesso! A recepção foi notificada.');
      setRemanejamentoDialog({
        open: false,
        agendamento: null,
        data: null,
        horaInicio: '',
        horariosDisponiveis: [],
        janelasLivres: { janelas: [], proximo_livre: null, ocupados: [], periodos: [] },
        diasDisponiveis: [],
        loadingDias: false,
        loadingHorarios: false,
        submitting: false
      });
      setCalendarRefreshKey(prev => prev + 1);
      fetchData();
    } catch (err) {
      // Governança HTTP 423: o interceptor global (AxiosGovernanceInterceptor)
      // já exibe o toast e redireciona para /minhas-pendencias. Não mostrar
      // outro toast aqui pra não sobrepor (e evitar "[object Object]" — o
      // `detail` do 423 é um objeto, não uma string).
      if (err?.response?.status !== 423) {
        const detail = err?.response?.data?.detail;
        const message = typeof detail === 'string'
          ? detail
          : (detail?.mensagem || 'Erro ao remanejar agendamento');
        toast.error(message);
      }
      setRemanejamentoDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  // Função para abrir modal de edição ou visualização
  const handleOpenEdit = (agendamento) => {
    // Para profissionais, primeiro mostra detalhes
    if (user?.perfil === 'profissional') {
      setDetailsDialog({
        open: true,
        agendamento: agendamento
      });
      return;
    }
    
    // Para admin/recepção, abre modal de edição completo
    openFullEditModal(agendamento);
  };
  
  // Abre modal de edição completa (admin/recepção)
  const openFullEditModal = (agendamento) => {
    setEditingAgendamento(agendamento);
    
    // Parse data e hora do agendamento
    const dataHoraInicio = parseLocalDateTime(agendamento.data_hora_inicio);
    const dataHoraFim = parseLocalDateTime(agendamento.data_hora_fim);
    const horaFormatada = format(dataHoraInicio, 'HH:mm');
    
    // Calcular duração em minutos
    const duracaoMinutos = Math.round((dataHoraFim - dataHoraInicio) / (1000 * 60));
    
    // Encontrar o profissional para pegar a especialidade
    const profissional = profissionais.find(p => p.id === agendamento.profissional_id);
    
    // Preencher horário manual para edição
    setHorarioManual(horaFormatada);
    
    setFormData({
      paciente_id: agendamento.paciente_id || '',
      profissional_id: agendamento.profissional_id || '',
      data: dataHoraInicio,
      hora_inicio: horaFormatada,
      duracao: duracaoMinutos || 40,
      convenio_id: agendamento.convenio_id || (agendamento.tipo_atendimento === 'particular' ? 'particular' : ''),
      observacoes: agendamento.observacoes || '',
      especialidade: profissional?.especialidade || '',
      recorrencia: 'nenhuma',
      recorrencia_fim: ''
    });
    
    // Filtrar profissionais pela especialidade
    if (profissional?.especialidade) {
      setProfissionaisFiltrados(profissionais.filter(p => p.especialidade === profissional.especialidade));
    }
    
    setIsDialogOpen(true);
  };

  const handlePacienteChange = (value) => {
    if (value === 'novo') {
      setIsNovoPaciente(true);
      setFormData({ ...formData, paciente_id: '', convenio_id: '' });
    } else {
      setIsNovoPaciente(false);
      // Buscar o paciente selecionado e preencher o convênio automaticamente
      const pacienteSelecionado = pacientes.find(p => p.id === value);
      const convenioId = pacienteSelecionado?.convenio_id || '';
      setFormData({ 
        ...formData, 
        paciente_id: value,
        convenio_id: convenioId
      });
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.agendado;
    const Icon = config.icon;
    return (
      <Badge 
        variant="outline"
        className="gap-1"
        style={{ borderColor: config.color, color: config.color }}
      >
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Função para calcular idade
  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return null;
    try {
      return differenceInYears(new Date(), parseISO(dataNascimento));
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agendamentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl md:text-3xl font-bold"
            style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
          >
            Agendamentos
          </h1>
          <p className="text-sm md:text-base" style={{ color: '#5C6B64', fontFamily: 'Figtree, sans-serif' }}>
            {format(selectedDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Botão Bloquear Agenda */}
          <Button
            variant="outline"
            data-testid="bloquear-agenda-button"
            className="gap-2 w-full sm:w-auto"
            style={{ borderColor: '#B75D69', color: '#B75D69', borderRadius: '0.75rem' }}
            onClick={() => openBloqueioModal()}
          >
            <CalendarOff className="h-4 w-4" />
            Bloquear Agenda
          </Button>
          
          {isAdminOrRecepcao() && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }} modal={false}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-agendamento-button"
                className="gap-2 w-full sm:w-auto"
                style={{ backgroundColor: '#4A6B5D', borderRadius: '0.75rem' }}
              >
                <Plus className="h-4 w-4" />
                Novo Agendamento
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingAgendamento ? 'Editar Agendamento' : 'Novo Agendamento'}
                </DialogTitle>
                <DialogDescription>
                  {editingAgendamento ? 'Altere os dados do agendamento' : 'Preencha os dados do agendamento'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Paciente */}
                <div className="space-y-2">
                  <Label>Paciente *</Label>
                  <Select
                    value={isNovoPaciente ? 'novo' : (formData.paciente_id || undefined)}
                    onValueChange={handlePacienteChange}
                  >
                    <SelectTrigger data-testid="agendamento-paciente-select">
                      <SelectValue placeholder="Selecione o paciente" />
                    </SelectTrigger>
                    <SelectContent 
                      position="popper" 
                      sideOffset={4}
                      className="max-h-[300px] overflow-y-auto"
                    >
                      {/* Opção de novo paciente */}
                      <SelectItem value="novo" className="font-medium">
                        <span className="flex items-center gap-2" style={{ color: '#4A6B5D' }}>
                          <UserPlus className="h-4 w-4" />
                          + Novo Paciente (cadastro rápido)
                        </span>
                      </SelectItem>
                      
                      {pacientes.length > 0 && (
                        <>
                          <Separator className="my-1" />
                          {pacientes.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nome_completo}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Formulário de Novo Paciente Rápido */}
                {isNovoPaciente && (
                  <div 
                    className="space-y-3 p-4 rounded-xl"
                    style={{ backgroundColor: '#F3F0EA', border: '1px dashed #D6C5B3' }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <UserPlus className="h-4 w-4" style={{ color: '#4A6B5D' }} />
                      <span className="font-medium text-sm" style={{ color: '#2C3632' }}>
                        Cadastro Rápido de Paciente
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Nome Completo *</Label>
                      <Input
                        data-testid="novo-paciente-nome"
                        value={novoPacienteData.nome_completo}
                        onChange={(e) => setNovoPacienteData({ ...novoPacienteData, nome_completo: e.target.value })}
                        placeholder="Nome do paciente"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Telefone</Label>
                        <Input
                          data-testid="novo-paciente-telefone"
                          value={novoPacienteData.telefone_responsavel}
                          onChange={(e) => setNovoPacienteData({ ...novoPacienteData, telefone_responsavel: e.target.value })}
                          placeholder="(15) 99999-9999"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Data Nascimento</Label>
                        <Input
                          data-testid="novo-paciente-nascimento"
                          type="date"
                          value={novoPacienteData.data_nascimento}
                          onChange={(e) => setNovoPacienteData({ ...novoPacienteData, data_nascimento: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Convênio / Particular *</Label>
                      <Select
                        value={novoPacienteData.convenio_id || undefined}
                        onValueChange={(value) => {
                          setNovoPacienteData({ ...novoPacienteData, convenio_id: value });
                          setFormData({ ...formData, convenio_id: value, profissional_id: '', hora_inicio: '' });
                        }}
                      >
                        <SelectTrigger data-testid="novo-paciente-convenio">
                          <SelectValue placeholder="Selecione o convênio ou Particular" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {convenios.map((c) => (
                            <SelectItem key={c.id} value={c.id} data-testid={`novo-paciente-convenio-opcao-${c.id}`}>
                              {c.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Especialidade */}
                <div className="space-y-2">
                  <Label>Especialidade *</Label>
                  <Select
                    value={formData.especialidade || undefined}
                    onValueChange={(value) => setFormData({ ...formData, especialidade: value, profissional_id: '', hora_inicio: '' })}
                  >
                    <SelectTrigger data-testid="agendamento-especialidade-select">
                      <SelectValue placeholder="Selecione a especialidade" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {ESPECIALIDADES.map((esp) => (
                        <SelectItem key={esp} value={esp}>
                          {esp}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Convênio - NOVO CAMPO antes do profissional */}
                <div className="space-y-2">
                  <Label>Convênio / Particular *</Label>
                  <Select
                    key={`convenio-${formData.paciente_id}`}
                    value={cobrancaSelecionadaValida ? (cobrancaParticular ? 'particular' : convenioIdSelecionado) : ''}
                    onValueChange={(value) => {
                      if (isNovoPaciente) {
                        setNovoPacienteData({ ...novoPacienteData, convenio_id: value === 'particular' ? (cadastroParticular?.id || '') : value });
                      }
                      setFormData({ ...formData, convenio_id: value, profissional_id: '', hora_inicio: '' });
                    }}
                  >
                    <SelectTrigger data-testid="agendamento-convenio-select" aria-required="true" aria-invalid={!cobrancaSelecionadaValida}>
                      <SelectValue placeholder="Selecione o convênio ou Particular" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="particular" data-testid="agendamento-convenio-opcao-particular">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Particular</span>
                          <span className="text-xs" style={{ color: '#5C6B64' }}>(60 min)</span>
                        </div>
                      </SelectItem>
                      {convenioHistoricoMantido && (
                        <SelectItem value={convenioIdSelecionado} disabled data-testid="agendamento-convenio-historico">
                          {editingAgendamento.convenios?.nome || 'Convênio anterior'} (inativo — histórico)
                        </SelectItem>
                      )}
                      {convenios.filter(c => c.id !== cadastroParticular?.id).map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`agendamento-convenio-opcao-${c.id}`}>
                          <div className="flex items-center gap-2">
                            <span>{c.nome}</span>
                            <span className="text-xs" style={{ color: '#5C6B64' }}>(40 min)</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!cobrancaSelecionadaValida && (
                    <p role="alert" className="text-sm text-amber-700" data-testid="agendamento-convenio-obrigatorio">
                      {convenioIdSelecionado
                        ? 'O convênio anterior não está disponível. Selecione outro convênio ativo ou Particular.'
                        : 'Selecione um convênio ativo ou Particular para continuar.'}
                    </p>
                  )}
                </div>

                {/* Profissional - filtrado por critérios */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Profissional *</Label>
                    {formData.especialidade && idadePacienteAtual !== null && (
                      <span className="text-xs" style={{ color: '#5C6B64' }}>
                        Paciente: {idadePacienteAtual} anos
                      </span>
                    )}
                  </div>
                  <Select
                    value={formData.profissional_id || undefined}
                    onValueChange={(value) => setFormData({ ...formData, profissional_id: value, hora_inicio: '' })}
                    disabled={!formData.especialidade}
                  >
                    <SelectTrigger data-testid="agendamento-profissional-select">
                      <SelectValue placeholder={formData.especialidade ? "Selecione o profissional" : "Selecione especialidade primeiro"} />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {profissionaisFiltrados.length === 0 ? (
                        <div className="p-3 text-sm text-center" style={{ color: '#B75D69' }}>
                          Nenhum profissional atende os critérios<br/>
                          <span className="text-xs" style={{ color: '#5C6B64' }}>
                            (especialidade e convênio)
                          </span>
                        </div>
                      ) : (
                        profissionaisFiltrados.map((p) => {
                          const foraFaixaEtaria = !p._dentroFaixaEtaria;
                          const mod = p.modalidade_agenda;
                          
                          return (
                            <SelectItem key={p.id} value={p.id}>
                              <div className="flex items-center gap-2">
                                {foraFaixaEtaria && (
                                  <span className="text-amber-500" title="Fora da faixa etária">⚠️</span>
                                )}
                                <span className={foraFaixaEtaria ? 'opacity-70' : ''}>{p.usuarios?.nome}</span>
                                <span className="text-xs" style={{ color: '#5C6B64' }}>· {p.especialidade}</span>
                                {mod && (
                                  <span
                                    className="text-[10px] px-1.5 py-0 leading-4 rounded-full border font-semibold"
                                    style={mod === 'locacao'
                                      ? { backgroundColor: '#DBEAFE', color: '#1E40AF', borderColor: '#93C5FD' }
                                      : { backgroundColor: '#EDE9FE', color: '#6D28D9', borderColor: '#C4B5FD' }}
                                    title={mod === 'locacao' ? 'Modalidade: Locação' : 'Modalidade: Percentual'}
                                  >
                                    {mod === 'locacao' ? 'LOC' : '%'}
                                  </span>
                                )}
                                {p.atende_aba && (
                                  <span className="text-xs px-1 py-0.5 rounded bg-[#4A6B5D] text-white">ABA</span>
                                )}
                                <span className="text-xs" style={{ color: foraFaixaEtaria ? '#B75D69' : '#8A9992' }}>
                                  ({p.faixa_etaria_min}-{p.faixa_etaria_max} anos)
                                </span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {/* Toggle rápido de modalidade — só aparece se o prof tem "gêmeo" de outra modalidade nos filtros */}
                  {formData.profissional_id && (() => {
                    const profAtual = profissionaisFiltrados.find(p => p.id === formData.profissional_id);
                    if (!profAtual || !profAtual.modalidade_agenda || !profAtual.usuario_id) return null;
                    const irmao = profissionaisFiltrados.find(p =>
                      p.id !== profAtual.id &&
                      p.usuario_id === profAtual.usuario_id &&
                      p.especialidade === profAtual.especialidade &&
                      p.modalidade_agenda &&
                      p.modalidade_agenda !== profAtual.modalidade_agenda
                    );
                    if (!irmao) return null;

                    const pillStyle = (mod, ativo) => {
                      const base = {
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: 999,
                        border: '1px solid',
                        cursor: ativo ? 'default' : 'pointer',
                        transition: 'all 120ms ease',
                      };
                      if (mod === 'locacao') {
                        return ativo
                          ? { ...base, backgroundColor: '#1E40AF', color: '#FFFFFF', borderColor: '#1E40AF' }
                          : { ...base, backgroundColor: '#F1F5F9', color: '#1E40AF', borderColor: '#CBD5E1' };
                      }
                      return ativo
                        ? { ...base, backgroundColor: '#6D28D9', color: '#FFFFFF', borderColor: '#6D28D9' }
                        : { ...base, backgroundColor: '#F1F5F9', color: '#6D28D9', borderColor: '#CBD5E1' };
                    };

                    const label = (mod) => mod === 'locacao' ? 'LOC' : '%';
                    const trocar = () => setFormData({ ...formData, profissional_id: irmao.id, hora_inicio: '' });

                    return (
                      <div
                        className="flex items-center gap-2 mt-2"
                        data-testid="modalidade-toggle"
                      >
                        <span className="text-[11px]" style={{ color: '#5C6B64' }}>Modalidade:</span>
                        <button
                          type="button"
                          style={pillStyle(profAtual.modalidade_agenda, true)}
                          aria-pressed="true"
                          data-testid={`modalidade-ativa-${profAtual.modalidade_agenda}`}
                        >
                          {label(profAtual.modalidade_agenda)}
                        </button>
                        <button
                          type="button"
                          onClick={trocar}
                          style={pillStyle(irmao.modalidade_agenda, false)}
                          title={`Trocar para ${label(irmao.modalidade_agenda)} — atualiza dias e horários disponíveis`}
                          data-testid={`modalidade-trocar-${irmao.modalidade_agenda}`}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = irmao.modalidade_agenda === 'locacao' ? '#DBEAFE' : '#EDE9FE';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F1F5F9';
                          }}
                        >
                          {label(irmao.modalidade_agenda)}
                        </button>
                        <span className="text-[10px] italic" style={{ color: '#8A9992' }}>
                          troca rápida — cada modalidade tem sua própria agenda
                        </span>
                      </div>
                    );
                  })()}
                  {formData.profissional_id && (() => {
                    const profSelecionado = profissionaisFiltrados.find(p => p.id === formData.profissional_id);
                    // Só mostra aviso se: profissional encontrado, idade do paciente calculada e fora da faixa
                    if (profSelecionado && idadePacienteAtual !== null && !profSelecionado._dentroFaixaEtaria) {
                      return (
                        <p className="text-xs mt-1 p-2 rounded" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                          ⚠️ Atenção: Profissional fora da faixa etária ({profSelecionado.faixa_etaria_min}-{profSelecionado.faixa_etaria_max} anos) do paciente ({idadePacienteAtual} anos)
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Data e Horário */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.data && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.data ? format(formData.data, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.data}
                          onSelect={(date) => date && setFormData({ ...formData, data: date, hora_inicio: '' })}
                          locale={ptBR}
                          disabled={(date) => {
                            // Datas passadas e domingos sempre desabilitados
                            if (date < new Date(new Date().setHours(0,0,0,0))) return true;
                            if (date.getDay() === 0) return true; // Domingo
                            
                            // Se tem profissional selecionado, verificar se o dia está disponível
                            if (formData.profissional_id && diasDisponiveis.length > 0) {
                              // Converter dia da semana JS (0=Dom, 1=Seg, ..., 6=Sab) para banco (1=Seg, ..., 6=Sab, 0=Dom)
                              const diaSemanaJS = date.getDay(); // 0=Dom, 1=Seg, etc.
                              const diaSemanaBanco = diaSemanaJS === 0 ? 0 : diaSemanaJS; // Dom=0, Seg=1, etc.
                              return !diasDisponiveis.includes(diaSemanaBanco);
                            }
                            
                            return false;
                          }}
                          modifiers={{
                            indisponivel: (date) => {
                              if (date.getDay() === 0) return false; // Domingo já tratado pelo disabled
                              if (!formData.profissional_id) return false;
                              if (diasDisponiveis.length === 0) return true;
                              const diaSemanaJS = date.getDay();
                              const diaSemanaBanco = diaSemanaJS === 0 ? 0 : diaSemanaJS;
                              return !diasDisponiveis.includes(diaSemanaBanco);
                            }
                          }}
                          modifiersClassNames={{
                            indisponivel: "text-gray-300 line-through"
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    {/* Aviso de feriado */}
                    {formData.data && verificarFeriado(formData.data) && (
                      <p className="text-xs mt-1 p-2 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                        ⚠️ {verificarFeriado(formData.data).nome} - A clínica não funciona em feriados
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Horário de Início *</Label>
                    {loadingHorarios ? (
                      <div className="flex items-center justify-center h-10 border rounded-md" style={{ borderColor: '#EAE6DF' }}>
                        <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#5C6B64' }} />
                      </div>
                    ) : !formData.profissional_id ? (
                      <div className="text-sm p-3 rounded-md" style={{ backgroundColor: '#F3F0EA', color: '#5C6B64' }}>
                        Selecione o profissional primeiro
                      </div>
                    ) : janelasLivres.janelas.length === 0 && horariosDisponiveis.length === 0 ? (
                      <div className="text-sm p-3 rounded-md" style={{ backgroundColor: '#FEF3F3', color: '#B75D69' }}>
                        Profissional não atende neste dia
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Próximo horário livre destacado */}
                        {janelasLivres.proximo_livre && (
                          <div 
                            className="p-3 rounded-lg cursor-pointer transition-all hover:shadow-md"
                            style={{ 
                              backgroundColor: horarioManual === janelasLivres.proximo_livre ? '#E8F5E9' : '#F0F7F4',
                              border: horarioManual === janelasLivres.proximo_livre ? '2px solid #4A6B5D' : '1px solid #C8E6C9'
                            }}
                            onClick={() => {
                              setHorarioManual(janelasLivres.proximo_livre);
                              setFormData({ ...formData, hora_inicio: janelasLivres.proximo_livre });
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" style={{ color: '#4A6B5D' }} />
                              <span className="font-semibold" style={{ color: '#4A6B5D' }}>
                                Próximo livre: {janelasLivres.proximo_livre}
                              </span>
                              {horarioManual === janelasLivres.proximo_livre && (
                                <CheckCircle className="h-4 w-4 ml-auto" style={{ color: '#4A6B5D' }} />
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Janelas de disponibilidade */}
                        {janelasLivres.janelas.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium" style={{ color: '#5C6B64' }}>Janelas disponíveis:</p>
                            <div className="flex flex-wrap gap-2">
                              {janelasLivres.janelas.map((j, idx) => (
                                <div 
                                  key={idx}
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: '#E8F0EB', color: '#4A6B5D' }}
                                >
                                  {j.inicio} - {j.fim} ({Math.floor(j.duracao_minutos / 60)}h{j.duracao_minutos % 60 > 0 ? `${j.duracao_minutos % 60}min` : ''} livre)
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Horários ocupados */}
                        {janelasLivres.ocupados.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium" style={{ color: '#B75D69' }}>Ocupados:</p>
                            <div className="flex flex-wrap gap-2">
                              {janelasLivres.ocupados.map((oc, idx) => (
                                <div 
                                  key={idx}
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: '#FDECEA', color: '#B75D69' }}
                                  title={oc.paciente}
                                >
                                  {oc.inicio} - {oc.fim}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Input de horário manual */}
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#5C6B64' }} />
                            <Input
                              type="time"
                              value={horarioManual}
                              onChange={(e) => {
                                setHorarioManual(e.target.value);
                                setFormData({ ...formData, hora_inicio: e.target.value });
                              }}
                              className="pl-10"
                              style={{ borderColor: '#EAE6DF' }}
                              data-testid="input-horario-manual"
                            />
                          </div>
                          <span className="text-xs" style={{ color: '#8A9992' }}>ou digite</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Duração com Spinner */}
                  <div className="space-y-2">
                    <Label>Duração</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          const newDuracao = Math.max(30, (formData.duracao || 40) - 5);
                          setFormData({ ...formData, duracao: newDuracao, _duracaoAjustadaManual: true });
                        }}
                        disabled={(formData.duracao || 40) <= 30}
                        style={{ borderColor: '#EAE6DF' }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      <div 
                        className="flex-1 text-center py-2 px-4 rounded-md font-medium"
                        style={{ 
                          backgroundColor: (formData.duracao || 40) > 60 ? '#FFF8E1' : '#F3F0EA',
                          color: (formData.duracao || 40) > 60 ? '#F57C00' : '#2C3632',
                          border: (formData.duracao || 40) > 60 ? '1px solid #FFE082' : '1px solid #EAE6DF'
                        }}
                      >
                        <Clock className="h-4 w-4 inline mr-2" style={{ color: '#5C6B64' }} />
                        {formData.duracao || 40} min
                        {(formData.duracao || 40) === 40 && <span className="text-xs ml-1" style={{ color: '#8A9992' }}>(padrão)</span>}
                      </div>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          const newDuracao = (formData.duracao || 40) + 5;
                          setFormData({ ...formData, duracao: newDuracao, _duracaoAjustadaManual: true });
                          if (newDuracao > 60) {
                            toast.warning('Atenção: Duração acima de 60 minutos!', { duration: 2000 });
                          }
                        }}
                        style={{ borderColor: '#EAE6DF' }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    {(formData.duracao || 40) > 60 && (
                      <p className="text-xs flex items-center gap-1" style={{ color: '#F57C00' }}>
                        <AlertCircle className="h-3 w-3" />
                        Consulta com duração estendida
                      </p>
                    )}
                  </div>
                </div>

                {/* Encaixe de Emergência */}
                <div 
                  className="p-4 rounded-xl space-y-3"
                  style={{ backgroundColor: formData.is_encaixe ? '#FEF3F3' : '#F5F5F5', border: formData.is_encaixe ? '1px solid #F5C6CB' : '1px solid #E0E0E0' }}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_encaixe"
                      checked={formData.is_encaixe}
                      onChange={(e) => setFormData({ ...formData, is_encaixe: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid="checkbox-encaixe"
                    />
                    <Label htmlFor="is_encaixe" className="cursor-pointer font-medium" style={{ color: formData.is_encaixe ? '#B75D69' : '#5C6B64' }}>
                      Encaixe de Emergência
                    </Label>
                    {formData.is_encaixe && (
                      <Badge style={{ backgroundColor: '#B75D69', color: 'white' }}>ENCAIXE</Badge>
                    )}
                  </div>
                  
                  {formData.is_encaixe && (
                    <div className="space-y-2">
                      <Label className="text-sm">Justificativa do Encaixe *</Label>
                      <Input
                        value={formData.justificativa_encaixe}
                        onChange={(e) => setFormData({ ...formData, justificativa_encaixe: e.target.value })}
                        placeholder="Ex: Paciente em crise, urgência médica..."
                        data-testid="input-justificativa-encaixe"
                      />
                      <p className="text-xs" style={{ color: '#B75D69' }}>
                        Encaixes ignoram validações de horário padrão. Use com responsabilidade.
                      </p>
                    </div>
                  )}
                </div>

                {/* Observações */}
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Observações do agendamento..."
                    rows={2}
                  />
                </div>

                {/* Recorrência */}
                <div 
                  className="p-4 rounded-xl space-y-4"
                  style={{ backgroundColor: '#F3F0EA' }}
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" style={{ color: '#4A6B5D' }} />
                    <Label className="font-medium">Agendamento Recorrente</Label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm" style={{ color: '#5C6B64' }}>Frequência</Label>
                      <Select
                        value={formData.recorrencia || undefined}
                        onValueChange={(value) => setFormData({ ...formData, recorrencia: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sem recorrência" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {RECORRENCIAS.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.recorrencia && formData.recorrencia !== 'nenhuma' && (
                      <div className="space-y-2">
                        <Label className="text-sm" style={{ color: '#5C6B64' }}>Repetir até</Label>
                        <Input
                          type="date"
                          value={formData.recorrencia_fim}
                          onChange={(e) => setFormData({ ...formData, recorrencia_fim: e.target.value })}
                          min={format(formData.data || new Date(), 'yyyy-MM-dd')}
                        />
                      </div>
                    )}
                  </div>
                  
                  {formData.recorrencia && formData.recorrencia !== 'nenhuma' && formData.recorrencia_fim && (
                    <p className="text-xs" style={{ color: '#5C6B64' }}>
                      Serão criados agendamentos {formData.recorrencia === 'semanal' ? 'toda semana' : formData.recorrencia === 'quinzenal' ? 'a cada 2 semanas' : 'todo mês'} até {format(new Date(formData.recorrencia_fim + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })}.
                      Os conflitos de horário serão ignorados automaticamente.
                    </p>
                  )}
                </div>
                
                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="agendamento-submit-button"
                    disabled={
                      !cobrancaSelecionadaValida ||
                      isSubmitting || 
                      (!formData.paciente_id && !isNovoPaciente) || 
                      !formData.profissional_id || 
                      !formData.especialidade || 
                      !formData.hora_inicio ||
                      (isNovoPaciente && !novoPacienteData.nome_completo)
                    }
                    style={{ backgroundColor: '#4A6B5D' }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {editingAgendamento ? 'Salvando...' : (isNovoPaciente ? 'Cadastrando...' : 'Agendando...')}
                      </>
                    ) : (
                      editingAgendamento ? 'Salvar Alterações' : (isNovoPaciente ? 'Cadastrar e Agendar' : 'Criar Agendamento')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Tabs de Visualização */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList className="grid w-full sm:w-auto grid-cols-3" style={{ backgroundColor: '#F3F0EA' }}>
            <TabsTrigger 
              value="semanal" 
              className="gap-2 data-[state=active]:bg-white"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              <CalendarIcon className="h-4 w-4" />
              Visão Semanal
            </TabsTrigger>
            <TabsTrigger 
              value="calendario" 
              className="gap-2 data-[state=active]:bg-white"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              <Grid3X3 className="h-4 w-4" />
              Calendário
            </TabsTrigger>
            <TabsTrigger 
              value="lista" 
              className="gap-2 data-[state=active]:bg-white"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              <List className="h-4 w-4" />
              Lista Diária
            </TabsTrigger>
          </TabsList>

          {/* Date Navigation - só aparece na view lista */}
          {activeView === 'lista' && (
            <div className="flex items-center gap-2 md:gap-3 flex-nowrap overflow-x-auto">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate(d => addDays(d, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2 flex-1 sm:flex-none">
                    <CalendarIcon className="h-4 w-4" />
                    {format(selectedDate, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate(d => addDays(d, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <Button
                variant="ghost"
                onClick={() => setSelectedDate(new Date())}
                style={{ color: '#4A6B5D' }}
                className="hidden sm:inline-flex"
              >
                Hoje
              </Button>
              
              {/* Campo de pesquisa por paciente */}
              <div className="relative w-40 sm:w-48 md:w-56 lg:w-64 shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchPacienteLista}
                  onChange={(e) => setSearchPacienteLista(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="search-paciente-lista"
                />
              </div>
            </div>
          )}
        </div>

        {/* Vista Semanal */}
        <TabsContent value="semanal" className="mt-4">
          <AgendaSemanalView 
            agendamentos={agendamentosSemana}
            salas={salas}
            profissionais={profissionais}
            loading={loadingSemana}
            onAgendamentoClick={(ag) => handleOpenEdit(ag)}
            onWeekChange={(weekStart, weekEnd) => fetchAgendamentosSemana(weekStart, weekEnd)}
            onNovoAgendamento={(dados) => {
              // Abrir modal de novo agendamento com dados pré-preenchidos
              const dataHora = new Date(dados.data_hora_inicio);
              const horaStr = `${dataHora.getHours().toString().padStart(2, '0')}:${dataHora.getMinutes().toString().padStart(2, '0')}`;
              
              setFormData(prev => ({
                ...prev,
                paciente_id: '',
                profissional_id: dados.profissional_id || '',
                data: dataHora,
                hora_inicio: horaStr,
                convenio_id: '',
                observacoes: '',
                recorrencia: 'nenhuma',
                is_encaixe: false,
              }));
              setIsDialogOpen(true);
            }}
            isProfissional={user?.perfil === 'profissional'}
          />
        </TabsContent>

        {/* Vista Calendário */}
        <TabsContent value="calendario" className="mt-4">
          <AgendaCalendarView 
            onAgendamentoClick={(ag) => {
              if (user?.perfil === 'profissional') {
                setDetailsDialog({ open: true, agendamento: ag });
              } else {
                handleOpenEdit(ag);
              }
            }}
            canDelete={false}
            refreshKey={calendarRefreshKey}
          />
        </TabsContent>

        {/* Vista Lista */}
        <TabsContent value="lista" className="mt-4">
      <Card 
        className="border shadow-sm"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
      >
        <CardHeader>
          <CardTitle className="text-lg" style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}>
            {(() => {
              const filtrados = agendamentos.filter(ag => !searchPacienteLista.trim() || ag.pacientes?.nome_completo?.toLowerCase().includes(searchPacienteLista.toLowerCase()));
              return `${filtrados.length} agendamento${filtrados.length !== 1 ? 's' : ''}${searchPacienteLista.trim() ? ` (filtrado de ${agendamentos.length})` : ''}`;
            })()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agendamentos.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4" style={{ color: '#EAE6DF' }} />
              <p style={{ color: '#5C6B64' }}>Nenhum agendamento para esta data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {agendamentos
                .filter(ag => !searchPacienteLista.trim() || ag.pacientes?.nome_completo?.toLowerCase().includes(searchPacienteLista.toLowerCase()))
                .map((ag) => (
                <div 
                  key={ag.id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl transition-colors hover:bg-[#F3F0EA] gap-3 cursor-pointer group"
                  style={{ backgroundColor: '#FDFBF7', border: '1px solid #EAE6DF' }}
                  data-testid={`agendamento-card-${ag.id}`}
                  onClick={() => (isAdminOrRecepcao() || user?.perfil === 'profissional') && handleOpenEdit(ag)}
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    {/* Horário */}
                    <div 
                      className="text-center min-w-[60px] md:min-w-[70px] p-2 md:p-3 rounded-xl relative"
                      style={{ backgroundColor: '#E8F0EB' }}
                    >
                      <p className="text-base md:text-lg font-bold" style={{ color: '#4A6B5D' }}>
                        {formatDateTime(ag.data_hora_inicio, 'HH:mm')}
                      </p>
                      <p className="text-xs" style={{ color: '#5C6B64' }}>
                        {formatDateTime(ag.data_hora_fim, 'HH:mm')}
                      </p>
                      {/* Edit icon on hover */}
                      {isAdminOrRecepcao() && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#4A6B5D]/80 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
                          <Edit className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate" style={{ color: '#2C3632' }}>
                        {ag.pacientes?.nome_completo}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1 text-xs md:text-sm" style={{ color: '#5C6B64' }}>
                        <span className="flex items-center gap-1 flex-wrap">
                          <User className="h-3 w-3" />
                          <span className="truncate max-w-[100px] md:max-w-none">{ag.profissionais?.usuarios?.nome}</span>
                          {(() => {
                            const uid = ag.profissionais?.usuario_id || ag.profissionais?.usuarios?.id;
                            const esp = ag.profissionais?.especialidade;
                            if (uid && esp && usuariosMultiEsp.has(uid)) {
                              return (
                                <span
                                  className="text-[10px] px-1.5 py-0 leading-4 rounded-full border font-medium"
                                  style={{ backgroundColor: '#F0F7F4', color: '#4A6B5D', borderColor: '#C6D9CE' }}
                                  title={`Especialidade: ${esp}`}
                                  data-testid={`agendamento-especialidade-${ag.id}`}
                                >
                                  {esp}
                                </span>
                              );
                            }
                            return null;
                          })()}
                          {ag.profissionais?.modalidade_agenda && (
                            <span
                              className="text-[10px] px-1.5 py-0 leading-4 rounded-full border font-semibold"
                              style={ag.profissionais.modalidade_agenda === 'locacao'
                                ? { backgroundColor: '#DBEAFE', color: '#1E40AF', borderColor: '#93C5FD' }
                                : { backgroundColor: '#EDE9FE', color: '#6D28D9', borderColor: '#C4B5FD' }}
                              title={ag.profissionais.modalidade_agenda === 'locacao' ? 'Modalidade: Locação' : 'Modalidade: Percentual'}
                              data-testid={`agendamento-modalidade-${ag.id}`}
                            >
                              {ag.profissionais.modalidade_agenda === 'locacao' ? 'LOC' : '%'}
                            </span>
                          )}
                        </span>
                        {ag.salas && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            Sala {ag.salas.numero}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                    {ag.is_reposicao && (
                      <Badge
                        data-testid={`badge-reposicao-lista-${ag.id}`}
                        variant="secondary"
                        className="text-xs font-bold"
                        style={{ backgroundColor: '#EDE9FE', color: '#5B21B6', border: '1px solid #C4B5FD' }}
                      >
                        REPOSIÇÃO
                      </Badge>
                    )}
                    {(ag.convenios?.nome || ag.tipo_atendimento === 'particular') && (
                      <Badge data-testid={`agendamento-cobranca-${ag.id}`} variant="outline" className="text-xs" style={{ borderColor: '#A3B19B', color: '#5C6B64' }}>
                        {ag.tipo_atendimento === 'particular' ? 'Particular' : ag.convenios.nome}
                      </Badge>
                    )}
                    {getStatusBadge(ag.status)}
                    
                    {/* Status Actions - Mostra apenas transições permitidas */}
                    {(() => {
                      const perfil = user?.perfil || 'paciente';
                      const transicoesPermitidas = getTransicoesPermitidas(ag.status, perfil);
                      
                      // Só mostra o select se há transições permitidas
                      if (transicoesPermitidas.length === 0) return null;
                      
                      return (
                        <Select
                          value={ag.status}
                          onValueChange={(value) => handleUpdateStatus(ag.id, value)}
                        >
                          <SelectTrigger className="w-[120px] md:w-[140px] text-xs md:text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={4}>
                            {/* Status atual */}
                            <SelectItem value={ag.status} disabled>
                              {STATUS_CONFIG[ag.status]?.label || ag.status}
                            </SelectItem>
                            {/* Transições permitidas */}
                            {transicoesPermitidas.map(status => (
                              <SelectItem key={status} value={status}>
                                {STATUS_CONFIG[status]?.label || status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                    
                    {/* Botão Remanejar - Apenas para Profissional com status agendado/aguardando/falta */}
                    {user?.perfil === 'profissional' && ['agendado', 'aguardando', 'falta'].includes(ag.status) && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 gap-1"
                          style={{ borderColor: '#4A6B5D', color: '#4A6B5D' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRemanejar(ag);
                          }}
                          data-testid="btn-remanejar"
                        >
                          <CalendarIcon className="h-3 w-3" />
                          Remanejar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 gap-1"
                          style={{ borderColor: '#8A6B4A', color: '#8A6B4A' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSwap(ag);
                          }}
                          data-testid="btn-trocar-horarios"
                          title="Trocar horário com outro paciente"
                        >
                          🔄 Trocar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
      
      {/* SerieDeleteDialog - Componente extraído (Fase 1 Refatoração) */}
      <SerieDeleteDialog
        dialogState={serieDeleteDialog}
        setDialogState={setSerieDeleteDialog}
        onDeleteSingle={(ag) => handleDeleteAgendamento(ag, false)}
        onDeleteSerie={(ag) => handleDeleteAgendamento(ag, true)}
      />
      
      {/* Modal de Remanejamento - Profissional */}
      <Dialog 
        open={remanejamentoDialog.open} 
        onOpenChange={(open) => !remanejamentoDialog.submitting && setRemanejamentoDialog(prev => ({ 
          ...prev, 
          open,
          data: null,
          horaInicio: ''
        }))}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
              📅 Remanejar Agendamento
            </DialogTitle>
            <DialogDescription>
              Selecione uma nova data e horário. A recepção será notificada automaticamente.
            </DialogDescription>
          </DialogHeader>
          
          {remanejamentoDialog.agendamento && (
            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#F5F2ED' }}>
              <p className="text-sm"><strong>Paciente:</strong> {remanejamentoDialog.agendamento.pacientes?.nome_completo}</p>
              <p className="text-sm"><strong>Horário atual:</strong> {formatDateTime(remanejamentoDialog.agendamento.data_hora_inicio, "dd/MM/yyyy 'às' HH:mm")}</p>
              <p className="text-sm"><strong>Duração:</strong> {remanejamentoDialog.agendamento.duracao || 40} minutos</p>
            </div>
          )}
          
          <div className="overflow-y-auto flex-1">
            {remanejamentoDialog.loadingDias ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#4A6B5D' }} />
                <span className="ml-2 text-sm" style={{ color: '#5C6B64' }}>Carregando disponibilidade...</span>
              </div>
            ) : remanejamentoDialog.diasDisponiveis.length === 0 ? (
              <div className="text-center py-8" style={{ color: '#5C6B64' }}>
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Você não possui dias de atendimento configurados.</p>
                <p className="text-xs mt-2">Entre em contato com a administração.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Calendário */}
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#2C3632' }}>
                    1. Selecione a Data
                  </label>
                  <div className="border rounded-lg p-2">
                    <Calendar
                      mode="single"
                      selected={remanejamentoDialog.data}
                      onSelect={(date) => {
                        if (date) {
                          buscarHorariosRemanejamento(date);
                        }
                      }}
                      locale={ptBR}
                      disabled={(date) => {
                        // Desabilitar datas passadas
                        if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                        // Desabilitar datas além de 30 dias
                        const limite = new Date();
                        limite.setDate(limite.getDate() + 30);
                        if (date > limite) return true;
                        // Desabilitar dias que o profissional não trabalha
                        const diaSemana = (date.getDay()) % 7; // 0=Domingo, 1=Segunda...
                        return !remanejamentoDialog.diasDisponiveis.includes(diaSemana);
                      }}
                      className="rounded-md"
                    />
                  </div>
                </div>
                
                {/* Horários */}
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#2C3632' }}>
                    2. Selecione o Horário
                  </label>
                  
                  {!remanejamentoDialog.data ? (
                    <div className="border rounded-lg p-4 text-center" style={{ color: '#5C6B64' }}>
                      <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Selecione uma data primeiro</p>
                    </div>
                  ) : remanejamentoDialog.loadingHorarios ? (
                    <div className="border rounded-lg p-4 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" style={{ color: '#4A6B5D' }} />
                      <span className="text-sm" style={{ color: '#5C6B64' }}>Buscando horários...</span>
                    </div>
                  ) : remanejamentoDialog.janelasLivres.janelas.length === 0 ? (
                    <div className="border rounded-lg p-4 text-center" style={{ color: '#5C6B64' }}>
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhum horário disponível nesta data</p>
                      {remanejamentoDialog.janelasLivres.message && (
                        <p className="text-xs mt-1">{remanejamentoDialog.janelasLivres.message}</p>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {/* Gerar slots a partir das janelas */}
                      {(() => {
                        const duracao = remanejamentoDialog.agendamento?.duracao || 40;
                        // Grade padrão da clínica é múltiplo de 20min. Usar passo
                        // de 20 min (não a duração) permite oferecer slots como
                        // 12:40 e 13:00 dentro de uma janela 12:40–13:40. Sem isso,
                        // Cibele Trevisan não conseguia remanejar para as 13:00
                        // em 02/09 mesmo com o horário livre (bug 2026-08).
                        const step = 20;
                        const slots = [];
                        const vistos = new Set();
                        
                        remanejamentoDialog.janelasLivres.janelas.forEach(janela => {
                          // Converter horários para minutos
                          const [hInicio, mInicio] = janela.inicio.split(':').map(Number);
                          const [hFim, mFim] = janela.fim.split(':').map(Number);
                          let inicioMin = hInicio * 60 + mInicio;
                          const fimMin = hFim * 60 + mFim;
                          
                          // Gerar slots com passo=20min, mas cada slot precisa
                          // caber em `duracao` dentro da janela.
                          while (inicioMin + duracao <= fimMin) {
                            const h = Math.floor(inicioMin / 60);
                            const m = inicioMin % 60;
                            const hora = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                            if (!vistos.has(hora)) {
                              slots.push(hora);
                              vistos.add(hora);
                            }
                            inicioMin += step;
                          }
                        });
                        
                        if (slots.length === 0) {
                          return (
                            <div className="text-center py-4" style={{ color: '#5C6B64' }}>
                              <p className="text-sm">Janelas muito curtas para a duração do atendimento</p>
                            </div>
                          );
                        }

                        // Ordenar por horário para o caso de múltiplas janelas
                        slots.sort();
                        
                        return (
                          <div className="grid grid-cols-3 gap-2">
                            {slots.map((hora, index) => {
                              const isSelected = remanejamentoDialog.horaInicio === hora;
                              return (
                                <Button
                                  key={index}
                                  variant={isSelected ? "default" : "outline"}
                                  size="sm"
                                  className={`text-xs ${isSelected ? 'text-white' : ''}`}
                                  style={isSelected ? { backgroundColor: '#4A6B5D' } : {}}
                                  onClick={() => setRemanejamentoDialog(prev => ({ 
                                    ...prev, 
                                    horaInicio: hora 
                                  }))}
                                >
                                  {hora}
                                </Button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      
                      {/* Info de disponibilidade */}
                      <div className="mt-3 pt-3 border-t space-y-1">
                        <p className="text-xs" style={{ color: '#5C6B64' }}>
                          Janelas disponíveis:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {remanejamentoDialog.janelasLivres.janelas.map((j, idx) => (
                            <span 
                              key={idx}
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: '#E8F0EB', color: '#4A6B5D' }}
                            >
                              {j.inicio} - {j.fim}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t mt-4">
            <Button 
              variant="outline" 
              onClick={() => setRemanejamentoDialog(prev => ({ 
                ...prev, 
                open: false,
                data: null,
                horaInicio: ''
              }))}
              disabled={remanejamentoDialog.submitting}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmarRemanejamento}
              disabled={!remanejamentoDialog.data || !remanejamentoDialog.horaInicio || remanejamentoDialog.submitting}
              style={{ backgroundColor: '#4A6B5D' }}
              className="text-white"
            >
              {remanejamentoDialog.submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Remanejando...
                </>
              ) : (
                'Confirmar Remanejamento'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CancelamentoDialog - Componente extraído (Fase 1 Refatoração) */}
      <CancelamentoDialog
        dialogState={cancelamentoDialog}
        setDialogState={setCancelamentoDialog}
        user={user}
        authFetch={authFetch}
        onConfirm={handleCancelarAgendamento}
      />

      {/* DetalhesAgendamentoDialog - Componente extraído (Fase 1 Refatoração) */}
      <DetalhesAgendamentoDialog
        dialogState={detailsDialog}
        setDialogState={setDetailsDialog}
        getStatusBadge={getStatusBadge}
        getTransicoesPermitidas={getTransicoesPermitidas}
        onUpdateStatus={handleUpdateStatus}
        onOpenRemanejar={handleOpenRemanejar}
      />

      {/* BloqueioAgendaDialog - Componente extraído (Fase 1 Refatoração) */}
      <BloqueioAgendaDialog
        dialogState={bloqueioDialog}
        setDialogState={setBloqueioDialog}
        formState={bloqueioForm}
        setFormState={setBloqueioForm}
        profissionais={profissionais}
        user={user}
        onSave={handleSaveBloqueio}
      />

      {/* Lista de Bloqueios Ativos */}
      {bloqueios.length > 0 && (
        <Card className="border mt-4" style={{ borderColor: '#EAE6DF' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}>
              <Ban className="h-5 w-5" style={{ color: '#B75D69' }} />
              Bloqueios Ativos ({bloqueios.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {bloqueios.map((bloqueio) => {
                const prof = profissionais.find(p => p.id === bloqueio.profissional_id);
                const tipoLabel = TIPOS_BLOQUEIO.find(t => t.value === bloqueio.tipo)?.label || bloqueio.tipo;
                
                return (
                  <div 
                    key={bloqueio.id}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: '#FEE8E8', border: '1px solid #EECDD0' }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm" style={{ color: '#B75D69' }}>
                        {prof?.usuarios?.nome || 'Profissional'} - {tipoLabel}
                      </p>
                      <p className="text-xs" style={{ color: '#8B5D65' }}>
                        {format(new Date(bloqueio.data_inicio + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR })}
                        {bloqueio.data_inicio !== bloqueio.data_fim && (
                          <> até {format(new Date(bloqueio.data_fim + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR })}</>
                        )}
                        {bloqueio.dia_inteiro ? (
                          <span className="ml-2">(Dia inteiro)</span>
                        ) : (
                          <span className="ml-2">({bloqueio.hora_inicio} - {bloqueio.hora_fim})</span>
                        )}
                      </p>
                      {bloqueio.motivo && (
                        <p className="text-xs mt-1 truncate" style={{ color: '#5C6B64' }}>
                          {bloqueio.motivo}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openBloqueioModal(bloqueio)}
                        className="h-8 w-8 p-0 hover:bg-white/50"
                        title="Editar bloqueio"
                      >
                        <Edit className="h-4 w-4" style={{ color: '#5C6B64' }} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBloqueio(bloqueio.id)}
                        className="h-8 w-8 p-0 hover:bg-red-100"
                        title="Remover bloqueio"
                      >
                        <Trash2 className="h-4 w-4" style={{ color: '#B75D69' }} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de TROCA de horários (swap) — Profissional / 2026-08 */}
      <Dialog
        open={swapDialog.open}
        onOpenChange={(open) => {
          if (!open && !swapDialog.submitting) {
            setSwapDialog({
              open: false, agendamentoA: null, candidatos: [], selectedBId: '',
              loading: false, submitting: false,
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: '#2C3632' }}>🔄 Trocar horários</DialogTitle>
            <DialogDescription>
              Selecione outro agendamento seu para inverter os horários dos dois pacientes.
              As salas também serão trocadas junto com o horário.
            </DialogDescription>
          </DialogHeader>

          {swapDialog.agendamentoA && (
            <div
              className="p-3 rounded-lg text-sm mb-3"
              style={{ backgroundColor: '#F5F3F0' }}
              data-testid="swap-origem-info"
            >
              <p><strong>Origem:</strong> {swapDialog.agendamentoA.pacientes?.nome_completo}</p>
              <p><strong>Horário atual:</strong> {formatDateTime(swapDialog.agendamentoA.data_hora_inicio, "dd/MM/yyyy 'às' HH:mm")}</p>
            </div>
          )}

          {swapDialog.loading ? (
            <div className="py-6 text-center text-sm" style={{ color: '#8A9992' }}>
              Carregando agendamentos elegíveis...
            </div>
          ) : swapDialog.candidatos.length === 0 ? (
            <div className="py-6 text-center text-sm" style={{ color: '#8A9992' }}>
              Nenhum outro agendamento elegível nos próximos 30 dias.
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="swap-candidatos-list">
              {swapDialog.candidatos.map(c => (
                <label
                  key={c.id}
                  className="flex items-start gap-2 p-2 rounded border cursor-pointer hover:bg-neutral-50"
                  style={{ borderColor: swapDialog.selectedBId === c.id ? '#4A6B5D' : '#E5E5E5' }}
                  data-testid={`swap-candidato-${c.id}`}
                >
                  <input
                    type="radio"
                    name="swap-candidato"
                    value={c.id}
                    checked={swapDialog.selectedBId === c.id}
                    onChange={() => setSwapDialog(prev => ({ ...prev, selectedBId: c.id }))}
                    className="mt-1"
                  />
                  <div className="text-sm flex-1">
                    <div className="font-medium" style={{ color: '#2C3632' }}>
                      {c.pacientes?.nome_completo || 'Paciente sem nome'}
                    </div>
                    <div className="text-xs" style={{ color: '#5C6B64' }}>
                      {formatDateTime(c.data_hora_inicio, "dd/MM/yyyy 'às' HH:mm")}
                      {c.status !== 'agendado' && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded" style={{ backgroundColor: '#F0E8DC', color: '#8A6B4A' }}>
                          {c.status}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setSwapDialog({
                open: false, agendamentoA: null, candidatos: [], selectedBId: '',
                loading: false, submitting: false,
              })}
              disabled={swapDialog.submitting}
              data-testid="btn-cancelar-troca"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSwap}
              disabled={!swapDialog.selectedBId || swapDialog.submitting}
              style={{ backgroundColor: '#8A6B4A', color: 'white' }}
              data-testid="btn-confirmar-troca"
            >
              {swapDialog.submitting ? 'Trocando...' : 'Confirmar Troca'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgendamentosPage;
